import React, { useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";
import { Modal, Tag, Typography, Alert, Skeleton, Empty, Tooltip, Button } from "antd";
import {
  ExclamationCircleFilled,
  CheckOutlined,
  Loading3QuartersOutlined,
  HourglassOutlined,
  FormOutlined,
  UploadOutlined,
  SendOutlined,
  SolutionOutlined,
  ArrowRightOutlined,
  ReloadOutlined,
  FileImageOutlined,
  RightOutlined,
  LoadingOutlined
} from "@ant-design/icons";
import moment from "moment-timezone";

import { Routes } from "../../../constant/routes";
import { EmrScreenshotsViewer } from "../../../screen/AgenticPlatform/EmrScreenshotsViewer";
import { formatMedicationLine } from "../../../screen/AgenticPlatform/taskHelpers";
import { selectIsPhiAllowed } from "../../../store/selector";
import {
  ERROR_50,
  ERROR_500,
  GRAY_100,
  GRAY_200,
  GRAY_50,
  GRAY_500,
  GRAY_600,
  GRAY_800,
  PRIMARY_100,
  PRIMARY_50,
  PRIMARY_600,
  SUCCESS_50,
  SUCCESS_500,
  WARNING_50,
  WARNING_500,
  WHITE
} from "../../../constant/colors";
import {
  findConsentStateCarrier,
  findOpenNavigatorPauses,
  getRunStatusMeta,
  isOpenConsentReview,
  isOpenScreenshotWait,
  currentAgentSteps,
  runStatusLabel,
  RESULT_ICON,
  hypercareReasonLabel,
  agentStepEvents
} from "./runStatuses";
import { runAttempts } from "./runView";
import { useGetSetting } from "../../../hooks/getSetting";
import {
  HITL_TASK_TYPES,
  AGENTIC_STEP_NAMES,
  AUDIT_STEP_RESULT,
  NAVIGATOR_PAUSE_TASK_TYPE_BY_STEP,
  REVIEW_DECISION,
  CONTACT_FIELD,
  FIN_ASSIST_REASON,
  NOT_ELIGIBLE_PREFIX,
  EMR_GATHERING_OUTCOME,
  CONSENT_SIGNATURE_WAIT_OUTCOME,
  CONSENT_SIGNATURE_DETECTION,
  ENROLLMENT_SKIP_REASON,
  CONSENT_REASON,
  SEND_CONSENT_OUTCOME,
  HYPERCARE_HANDOFF_OUTCOME,
  RUN_FINAL_OUTCOME
} from "../../../constant/agenticPlatform";

const { Text } = Typography;

// FE-only synthetic pending-action kind: the screenshots operator-wait isn't a
// navigator pause (it lives inside EMR gathering, keyed off the run's
// waiting_hitl + hitlTaskType=emr_screenshots pair), so it has
// no platform enum. Distinct from the two `HITL_TASK_TYPES` kinds.
const PENDING_ACTION_SCREENSHOTS = "screenshots";
const PENDING_ACTION_SCREENSHOTS_PROCESSING = "screenshots_processing";
// FE-only synthetic kind for a FAN-OUT wait: several navigator pauses are open
// at once (one express-enrollment child per enrollable pap, each parked on its
// own review). No platform enum either — it isn't a pause, it's the absence of
// a single one, so the banner names the count and sends the navigator to the
// hub to pick.
const PENDING_ACTION_MULTIPLE = "multiple";

// ─── Step result presentation ─────────────────────────────────────────────
// Each result maps to an icon + a foreground / soft-background color pair.
// The soft background is used for the result-chip; the foreground for the
// icon. SKIPPED reuses neutral grey to visually downweight it.

// Keys are lowercase to match the DB enum values stored in
// `audit_step.result` ("passed" / "failed" / "skipped"). Looking up by
// uppercase would silently fall through to the SKIPPED default for
// every step, making the whole UI look like nothing executed.
/**
 * The timeline's glyph for a step that PASSED — a bare check, not a filled disc.
 * Most rows in a run passed, and forty blue discs compete with the step names
 * they sit beside; a check does the same job quietly.
 *
 * PASSED ONLY, deliberately. Every other result uses a glyph that carries a
 * distinction on its own: a failure the run RECOVERED past shows an amber
 * exclamation where a terminal one shows a red cross, and that pairing is
 * asserted in its own test block. Flattening them to one outlined mark left
 * only colour to tell them apart.
 */
const TIMELINE_PASSED_ICON = CheckOutlined;

const STEP_RESULT_VISUAL = {
  [AUDIT_STEP_RESULT.PASSED]: { icon: RESULT_ICON.passed, fg: PRIMARY_600, bg: SUCCESS_50, label: "Passed" },
  [AUDIT_STEP_RESULT.FAILED]: { icon: RESULT_ICON.failed, fg: ERROR_500, bg: ERROR_50, label: "Failed" },
  // An expiry reads grey, like SKIPPED: nothing broke and nothing was decided.
  // It used to arrive as FAILED, so the row showed a red error next to a neutral
  // "Not reviewed in time" pill that contradicted it.
  [AUDIT_STEP_RESULT.TIMED_OUT]: { icon: RESULT_ICON.skipped, fg: GRAY_500, bg: GRAY_100, label: "Timed out" },
  [AUDIT_STEP_RESULT.SKIPPED]: { icon: RESULT_ICON.skipped, fg: GRAY_500, bg: GRAY_100, label: "Skipped" },
  // A step a restart abandoned mid-flight. Grey like SKIPPED — nothing broke —
  // but its own word: "Skipped" claims the agent decided not to do the work,
  // when in fact the work started and was thrown away with the attempt.
  [AUDIT_STEP_RESULT.SUPERSEDED]: { icon: RESULT_ICON.skipped, fg: GRAY_500, bg: GRAY_100, label: "Superseded" }
};

// A step that FAILED but the run CONTINUED PAST it (a later step exists) — the
// failure was overcome, not terminal, so it reads as an orange warning rather
// than a hard red error. Same shape as STEP_RESULT_VISUAL.failed but with the
// amber `ExclamationCircleFilled` in WARNING_500 (the file's standard warning
// foreground, shared with WAITING_VISUAL + the negative verdict pill). The
// canonical case: an `emr_gathering` step that failed to match the patient
// (e.g. operator_done_wrong_patient) but EMR-fill recovered the run and it
// reached consent.
const RECOVERED_FAILURE_VISUAL = {
  icon: ExclamationCircleFilled,
  fg: WARNING_500,
  bg: WARNING_50,
  label: "Recovered"
};

// In-progress step where the AGENT is actively executing — spinning
// blue loader. Distinct from human-wait states (see WAITING_VISUAL).
const RUNNING_VISUAL = {
  icon: Loading3QuartersOutlined,
  fg: PRIMARY_600,
  bg: GRAY_100,
  label: "Running",
  // Marker used by render sites to decide whether the icon should
  // spin. Survives object spread, unlike the alternative of checking
  // `visual === RUNNING_VISUAL`.
  running: true
};

// In-progress step where the workflow is PAUSED on a human action —
// orange hourglass, static (no spin). Communicates "we're not
// processing, we're waiting on you / someone else." Multi-day waits
// shouldn't look like the agent is busy.
const WAITING_VISUAL = {
  icon: HourglassOutlined,
  fg: WARNING_500,
  bg: WARNING_50,
  label: "Waiting",
  running: false
};

// Resolves the visual to use for a step that hasn't completed yet
// (`step.result == null`). Default is the active-agent "Running"
// spinner; specific (stepName, outcome) pairs surface as the orange
// human-wait state instead.
//
// Mental model: blue spinner = agent is doing something. Orange
// hourglass = a human needs to do something. Survives the
// computer-use transition (just changes which states fall in which
// bucket — when an outcome no longer represents a human wait, drop
// its entry from this map and it falls back to RUNNING).
//
// `emr_gathering` has two in-progress sub-phases, each rendered
// distinctly so the operator can tell which side of the submit they're
// on:
//   - `waiting_for_screenshots`   — operator hasn't submitted; orange
//                                   hourglass (human action needed)
//   - `processing_screenshots`    — operator submitted; blue spinner
//                                   (agent now extracting + matching)
function visualForInProgress(step) {
  if (step?.stepName === AGENTIC_STEP_NAMES.EMR_GATHERING) {
    if (step.outcome === EMR_GATHERING_OUTCOME.WAITING_FOR_SCREENSHOTS) {
      return { ...WAITING_VISUAL, label: "Waiting for screenshots" };
    }
    if (step.outcome === EMR_GATHERING_OUTCOME.PROCESSING_SCREENSHOTS) {
      return { ...RUNNING_VISUAL, label: "Processing screenshots" };
    }
  }
  // An emr_fill pause is a human-wait (the navigator must enter EMR data), not
  // the agent running — show the orange "Waiting for navigator" hourglass. Unlike
  // consent_decide it carries no verdict pill, so this chip is its only
  // awaiting signal; without it a still-open fill misreads as "Running".
  // The ee_review pause has that same shape (a human-wait with no verdict pill),
  // and sits inside a COLLAPSED enrollment panel where the banner isn't in view.
  if (step?.stepName === AGENTIC_STEP_NAMES.EMR_MANUAL_FILL || step?.stepName === AGENTIC_STEP_NAMES.EE_HUMAN_REVIEW) {
    return { ...WAITING_VISUAL, label: "Waiting for navigator" };
  }
  // The post-send signature wait is a human-wait too, but the human is the
  // PATIENT — there is no task, nothing for a navigator to action, and nothing
  // for them to be blamed for. Naming it explicitly keeps it out of the
  // "Waiting for navigator" bucket while still reading as a wait, not as the agent
  // grinding away (which "Running" would imply for days).
  if (step?.stepName === AGENTIC_STEP_NAMES.CONSENT_SIGNATURE_WAIT) {
    return { ...WAITING_VISUAL, label: "Waiting for patient" };
  }
  return RUNNING_VISUAL;
}

// ─── Step verdicts ────────────────────────────────────────────────────────
// `step.result` is technical-only (passed = ran cleanly, failed = error,
// skipped = step decided to do nothing). The eligibility verdict — eligible /
// not eligible / oop met / consent expired / etc. — is a separate dimension
// that the agent records on `reason` + `payload`. `stepVerdict` maps a step to
// one of four TONES (`STEP_TONE`) so navigators see the gate outcome at a
// glance, with each color carrying exactly one meaning:
//
//   success   (green) — the agent found a path / acted / progressed.
//   neutral   (gray)  — informational: no action, autonomously closed, or a
//                       point-in-time state (also the muted treatment for a
//                       verdict that's been superseded by a later step — the
//                       pill stays so the audit story is whole, but it's no
//                       longer the live call to action).
//   attention (amber) — a navigator declined, or a recoverable hiccup that
//                       someone may want to look at (e.g. a send blocked on
//                       missing info, or a recovered EMR-gather mismatch).
//   failure   (red)   — a genuine, unrecovered failure / crash.
//
// Previously amber was overloaded (neutral outcomes, real failures AND
// attention all shared it) and "Ready to send" rendered in three colors; the
// single tone map gives every color one job. Running/waiting in-progress chips
// are NOT verdict pills (they keep RUNNING_VISUAL / WAITING_VISUAL blue/amber),
// and the leading status ICONS (STEP_RESULT_VISUAL / RECOVERED_FAILURE_VISUAL)
// are a separate dimension this map doesn't touch.
const STEP_TONE = {
  success: { fg: SUCCESS_500, bg: SUCCESS_50 },
  neutral: { fg: GRAY_600, bg: GRAY_100 },
  attention: { fg: WARNING_500, bg: WARNING_50 },
  failure: { fg: ERROR_500, bg: ERROR_50 }
};

// `recoveredFromFailure` is the run-level recovered flag (the SAME
// `isRecoveredFailure` derivation that drives the amber exclamation icon — see
// RunDetails): true when this failed step has a later successor, so the run
// continued past it. It only matters for the two EMR-gather mismatch verdicts
// ("Wrong patient" / "No screenshots"), which read as amber `attention` while
// the run recovered and red `failure` when terminal — so the pill tone matches
// the leading icon. ("Extraction failed" / "No uploads" aren't recoverable
// EMR-gather outcomes, so they're always red.)
const stepVerdict = (step, recoveredFromFailure, t) => {
  const reason = step.outcome;
  const payload = step.payload || {};
  switch (step.stepName) {
    case AGENTIC_STEP_NAMES.EMR_MANUAL_FILL:
      // The manual-fill pause is a fill, not a verdict — the navigator entered
      // the EMR data, they didn't approve a recommendation. So it carries NO
      // verdict pill (a green "Approved" misrepresents what happened); the
      // "Filled by X" attribution + the entered insurances/meds block in the
      // step body convey it instead. Only real decisions get a verdict pill.
      return null;
    case AGENTIC_STEP_NAMES.CONSENT_HUMAN_REVIEW: {
      // The HITL pause's "what happened" pill — tone by sentiment, label from
      // result + decision. Approve = success (green); decline = attention
      // (amber, a navigator chose not to proceed); timeout (result "timed_out")
      // = neutral gray (nobody acted — informational, not a crash);
      // still-awaiting = neutral gray (no completed call to action yet). The
      // label text is i18n'd via `humanReviewOutcomeText`; only the tone is
      // chosen here.
      const label = humanReviewOutcomeText(step, t);
      if (!label) return null;
      if (step.result == null) return { ...STEP_TONE.neutral, label };
      if (payload.decision === REVIEW_DECISION.DECLINE) return { ...STEP_TONE.attention, label };
      if (step.result === AUDIT_STEP_RESULT.TIMED_OUT) return { ...STEP_TONE.neutral, label };
      return { ...STEP_TONE.success, label };
    }
    case AGENTIC_STEP_NAMES.FINANCIAL_ASSISTANCE_ELIGIBILITY:
      // Reason-driven: "Eligible" when at least one hypercare reason's predicate
      // passed; the per-reason breakdown below explains which.
      if (reason === FIN_ASSIST_REASON.ELIGIBLE) {
        return { ...STEP_TONE.success, label: "Eligible" };
      }
      // Not eligible is an informational outcome (no reason passed), not an
      // error or something needing attention → neutral gray.
      if (reason && reason.startsWith(NOT_ELIGIBLE_PREFIX)) return { ...STEP_TONE.neutral, label: "Not eligible" };
      return null;
    case AGENTIC_STEP_NAMES.REMAINING_OOP_CALCULATION: {
      // Verdict comes from payload.eligible — the step always runs (BI + a
      // coverage read). `remainingOops` is the per-active-coverage remaining OOP.
      const remainingOops = Array.isArray(payload.remainingOops) ? payload.remainingOops : [];
      const hasKnownRemaining = remainingOops.some((v) => v != null);
      // OOP met is a real autonomous terminal, NOT a skip — surface it (neutral
      // gray: an informational terminal state, not an error).
      if (payload.eligible === false) return { ...STEP_TONE.neutral, label: "OOP met" };
      // Eligible with at least one known coverage figure → genuinely has OOP left
      // to offset (can proceed) → success green.
      if (payload.eligible === true && hasKnownRemaining) return { ...STEP_TONE.success, label: "OOP not met" };
      // Eligible but no coverage figures (FN had nothing) — no verdict pill; the
      // summary line shows "N/A". A green verdict would be a lie.
      return null;
    }
    case AGENTIC_STEP_NAMES.CONSENT_STATE_CHECK:
      // Pill labels mirror the consent-state vocabulary the navigator already
      // sees in the Tasks hub (HitlTaskModal / agentic_board.steps_summary):
      //   "Ready to send"   = eligible, awaiting the navigator's Send decision
      //   "Active consent"  = patient already covered, no action
      //   "Link in progress" = a consent link is in flight, observe only
      //   "Blocked"         = data/state fix needed (missing info / expired / revoked)
      // The consent step surfaces the *state*; the Send / Don't-send decision
      // lives on the human_review step's pill, not here.
      //
      // "Active consent", "Ready to send" and "Link in progress" are all
      // informational states (no failure, no action recorded on this step) →
      // neutral gray. "Ready to send" is a single gray regardless of whether
      // the decision step has landed yet — it's a state, not a call to action.
      // "Blocked" is the one consent state someone must fix → attention amber.
      if (reason === CONSENT_REASON.PATIENT_HAS_ACTIVE_CONSENT)
        return { ...STEP_TONE.neutral, label: "Active consent" };
      if (reason === CONSENT_REASON.ELIGIBLE_TO_SEND) return { ...STEP_TONE.neutral, label: "Ready to send" };
      if (reason === CONSENT_REASON.CONTACT_INFO_MISSING) return { ...STEP_TONE.attention, label: "Blocked" };
      if (reason === CONSENT_REASON.CONSENT_LINK_IN_PROGRESS)
        return { ...STEP_TONE.neutral, label: "Link in progress" };
      if (
        reason === CONSENT_REASON.CONSENT_LINK_EXPIRED ||
        reason === CONSENT_REASON.CONSENT_LINK_REVOKED ||
        reason === CONSENT_REASON.CONSENT_EXPIRED ||
        reason === CONSENT_REASON.CONSENT_REVOKED
      ) {
        return { ...STEP_TONE.attention, label: "Blocked" };
      }
      return null;
    case AGENTIC_STEP_NAMES.CONSENT_SIGNATURE_WAIT:
      // The title stays the STAGE ("Consent signature"); what became of it rides
      // in the pill, like every neighbouring row. Without a pill the title had to
      // carry the outcome too, so a resolved wait still read "…wait" after the
      // patient had already signed.
      //
      // `not_signed` is neutral, not attention: the window closing is a normal
      // ending the patient simply didn't act on, and amber would read as
      // something a navigator failed to do.
      if (step.outcome === CONSENT_SIGNATURE_WAIT_OUTCOME.SIGNED) return { ...STEP_TONE.success, label: "Signed" };
      if (step.outcome === CONSENT_SIGNATURE_WAIT_OUTCOME.NOT_SIGNED)
        return { ...STEP_TONE.neutral, label: "Not signed" };
      return null;
    case AGENTIC_STEP_NAMES.SEND_CONSENT:
      // The send step writes outcome='sent' (happy path) or a ConsentReason
      // (a re-check found a state shift). A dispatch failure crashes the run
      // (step FAILED, payload.error) — rendered by the crash-step path.
      if (reason === SEND_CONSENT_OUTCOME.SENT) return { ...STEP_TONE.success, label: "Sent" };
      if (reason === CONSENT_REASON.PATIENT_HAS_ACTIVE_CONSENT)
        return { ...STEP_TONE.neutral, label: "Active consent" };
      if (reason === CONSENT_REASON.CONSENT_LINK_IN_PROGRESS)
        return { ...STEP_TONE.neutral, label: "Link in progress" };
      if (reason === CONSENT_REASON.CONTACT_INFO_MISSING) return { ...STEP_TONE.attention, label: "Blocked" };
      if (
        reason === CONSENT_REASON.CONSENT_LINK_EXPIRED ||
        reason === CONSENT_REASON.CONSENT_LINK_REVOKED ||
        reason === CONSENT_REASON.CONSENT_EXPIRED ||
        reason === CONSENT_REASON.CONSENT_REVOKED
      ) {
        return { ...STEP_TONE.attention, label: "Blocked" };
      }
      return null;
    case AGENTIC_STEP_NAMES.EMR_GATHERING:
      // Terminal verdicts for the gathering step. `operator_done` +
      // `already_fresh` mean the workflow has usable data; the four
      // operator-done-no-data subcases each flag a different problem. In-progress
      // outcomes (waiting_for_screenshots / processing_screenshots) hit
      // `visualForInProgress` above and don't pass through here.
      //
      // "Wrong patient" / "No screenshots" are tone-by-recovery: amber
      // `attention` when the run continued past the failure, red `failure` when
      // terminal — so the pill matches the leading icon. "Extraction failed" /
      // "No uploads" are genuine, non-recoverable failures → always red.
      if (reason === EMR_GATHERING_OUTCOME.OPERATOR_DONE || reason === EMR_GATHERING_OUTCOME.ALREADY_FRESH) {
        return { ...STEP_TONE.success, label: "Extracted" };
      }
      if (reason === EMR_GATHERING_OUTCOME.OPERATOR_DONE_WRONG_PATIENT) {
        return { ...(recoveredFromFailure ? STEP_TONE.attention : STEP_TONE.failure), label: "Wrong patient" };
      }
      if (reason === EMR_GATHERING_OUTCOME.OPERATOR_DONE_EXTRACTION_FAILED) {
        return { ...STEP_TONE.failure, label: "Extraction failed" };
      }
      if (reason === EMR_GATHERING_OUTCOME.OPERATOR_DONE_NO_UPLOADS) {
        return { ...STEP_TONE.failure, label: "No uploads" };
      }
      if (reason === EMR_GATHERING_OUTCOME.NO_SCREENSHOTS_PROVIDED) {
        return { ...(recoveredFromFailure ? STEP_TONE.attention : STEP_TONE.failure), label: "No screenshots" };
      }
      if (reason === EMR_GATHERING_OUTCOME.GATHERING_ERROR) {
        return {
          ...(recoveredFromFailure ? STEP_TONE.attention : STEP_TONE.failure),
          label: t("agentic_runs.verdict.gathering_error")
        };
      }
      if (reason === EMR_GATHERING_OUTCOME.OPERATOR_DONE_PARTIAL_DATA) {
        return {
          ...(recoveredFromFailure ? STEP_TONE.attention : STEP_TONE.failure),
          label: t("agentic_runs.verdict.partial_data")
        };
      }
      return null;
    default:
      // EMR + update_* steps don't have a verdict — they fetch data /
      // apply a side-effect. No pill rendered.
      return null;
  }
};

// ─── Reason-code → friendly text ──────────────────────────────────────────
// Stable reason codes ride on `audit_step.outcome` and `audit_run.finalOutcome`
// for analytics and platform-side logic. They're terse and meant for
// machine consumption — `not_eligible`, `oop_met`, etc.
// Navigators reading the audit modal want a human sentence; this dict
// translates each code into one. Codes without a mapping fall through to
// the raw code so missing entries are visible rather than hidden.
const REASON_FRIENDLY_TEXT = {
  // Financial-assistance step (FINANCIAL_ASSISTANCE_REASONS in the workflow).
  [FIN_ASSIST_REASON.ELIGIBLE]: "At least one hypercare reason is eligible — see the per-reason breakdown.",
  [FIN_ASSIST_REASON.NOT_ELIGIBLE_NO_INSURANCE_DATA]: "Patient has no insurance data on record.",
  [FIN_ASSIST_REASON.NOT_ELIGIBLE_NO_REASONS]: "No hypercare reasons were provided for this run.",
  [FIN_ASSIST_REASON.NO_ACTIVE_MEDICATIONS]:
    "Patient has no active medication to assist with — not in active treatment.",

  // Run finalOutcome (RxConductorFinalOutcome).
  [RUN_FINAL_OUTCOME.FAILURE]:
    "The agent hit a technical error and couldn't finish — the patient was returned to the queue for a navigator.",
  [RUN_FINAL_OUTCOME.NOT_ELIGIBLE]: "The agent couldn't find an enrollment path for this patient.",
  [RUN_FINAL_OUTCOME.OOP_MET]: "Patient has already met their out-of-pocket maximum for the year.",
  // The consent went out and the patient did NOT sign inside the wait window, so
  // the agent stopped there. It no longer sets a follow-up — the queue rows going
  // back to the board ARE the handback.
  [RUN_FINAL_OUTCOME.CONSENT_SENT_BY_NAVIGATOR]:
    "The consent link was sent but the patient didn't sign it in time — the patient is back on the queue for you.",
  // Pre-MVP-31690 runs merged both enrollment handbacks under one code; the two
  // below split them, so a reader learns WHICH happened.
  [RUN_FINAL_OUTCOME.CONSENT_OBTAINED_HANDOFF]:
    "Consent is in hand, but there was no program the agent could enroll into — the patient is back on the queue for you.",
  [RUN_FINAL_OUTCOME.NOTHING_ENROLLABLE]:
    "Consent is in hand, but none of the eligible programs are set up for automatic enrollment — the patient is back on the queue for you.",
  [RUN_FINAL_OUTCOME.ENROLLMENT_NONE_SUBMITTED]:
    "Consent is in hand and the agent started the enrollments, but none went through — see each enrollment below. The patient is back on the queue for you.",
  // The conductor's consent-decline ending; the generic REVIEW_DECLINED below
  // stays for express-enrollment child rows (their ee_review declines). The
  // decline reason is recorded on the human_review step.
  [RUN_FINAL_OUTCOME.FN_CONSENT_SENDING_DECLINED]:
    "Navigator declined to send the consent — patient remains on the queue for manual handling.",
  [RUN_FINAL_OUTCOME.REVIEW_DECLINED]:
    "Navigator declined the agent's recommendation — patient remains on the manual queue for manual handling.",
  [CONSENT_REASON.PATIENT_HAS_ACTIVE_CONSENT]: "Patient already has active consent — navigator can proceed manually.",
  // Express-enrollment verdicts. Partial is a real outcome, not a failure: the
  // enrolled programs are done and closed, and only the rest came back.
  [RUN_FINAL_OUTCOME.ENROLLED]: "Every eligible program was enrolled — nothing left for a navigator here.",
  [RUN_FINAL_OUTCOME.ENROLLMENT_PARTIAL]:
    "Some eligible programs enrolled and some didn't — the ones that didn't are back on the queue. See each enrollment below.",
  [EMR_GATHERING_OUTCOME.NO_SCREENSHOTS_PROVIDED]: "No EMR screenshots were provided for this patient.",

  // EMR-gathering step (EmrGatheringExitMode). Operator-done splits
  // diagnose the "no usable extraction" subcases — the first one is
  // a success and shows up under the green-check verdict, the rest
  // are failures the navigator/operator can act on.
  [EMR_GATHERING_OUTCOME.OPERATOR_DONE]: "EMR screenshots extracted successfully for this patient.",
  [EMR_GATHERING_OUTCOME.OPERATOR_DONE_WRONG_PATIENT]:
    "Screenshots were extracted, but the patient identity didn't match this task.",
  [EMR_GATHERING_OUTCOME.OPERATOR_DONE_EXTRACTION_FAILED]:
    "All EMR screenshot extractions failed technically (image / model error). Try re-uploading.",
  [EMR_GATHERING_OUTCOME.OPERATOR_DONE_NO_UPLOADS]: "Task was submitted with no screenshots — nothing to extract.",
  [EMR_GATHERING_OUTCOME.ALREADY_FRESH]: "Recent EMR-extracted data already exists for this patient; reusing it.",
  [EMR_GATHERING_OUTCOME.WAITING_FOR_SCREENSHOTS]:
    "Waiting for an operator to upload EMR screenshots in the EMR Tasks tab.",
  [EMR_GATHERING_OUTCOME.GATHERING_ERROR]:
    "Screenshot gathering hit a technical error — the run fell back to a manual EMR fill.",
  [EMR_GATHERING_OUTCOME.OPERATOR_DONE_PARTIAL_DATA]:
    "The screenshots covered only part of the record (e.g. insurance without medications) — the missing part needs a manual fill.",
  [EMR_GATHERING_OUTCOME.PROCESSING_SCREENSHOTS]: "Operator submitted — extracting + matching screenshots.",

  // Consent-step reasons (ConsentReason).
  [CONSENT_REASON.ELIGIBLE_TO_SEND]: "Patient is ready to receive a consent link.",
  [CONSENT_REASON.CONSENT_LINK_IN_PROGRESS]: "Consent link is in progress — waiting on the patient.",
  [CONSENT_REASON.CONSENT_LINK_EXPIRED]: "The consent link sent to the patient has expired.",
  [CONSENT_REASON.CONSENT_LINK_REVOKED]: "The consent link was revoked.",
  [CONSENT_REASON.CONSENT_REVOKED]: "Patient consent was revoked.",
  [CONSENT_REASON.CONSENT_EXPIRED]: "Patient consent has expired.",
  [CONSENT_REASON.CONTACT_INFO_MISSING]: "Patient is missing contact info needed to send consent."
};

const friendlyReason = (code) => (code && REASON_FRIENDLY_TEXT[code]) || null;

/**
 * Why a run handed its queue rows back, as a CLAUSE — for the
 * `hypercare_handoff` row, which supplies its own "back in hypercare…"
 * lead. `REASON_FRIENDLY_TEXT` can't be reused there: those entries are whole
 * standalone sentences that already state the patient is back on the queue.
 *
 * Only handback endings belong here. A missing entry falls back to the run's
 * `detail`, then to the bare lead — never to a raw outcome code.
 */
/**
 * Per-program "why the agent can't take this one", in navigator terms.
 *
 * `PROGRAM_NOT_CONFIGURED` is a TailorMed setup gap, not a patient problem, and
 * the copy has to say so — phrasing it as a failed check would read as the
 * patient being ineligible when they are not.
 *
 * Nothing here is about the PATIENT any more: the age/caregiver entries were the
 * demo guards, and who needs a caregiver is now the vendor form's own rule,
 * answered at the `ee_review` pause like any manual enrollment.
 */
const SKIP_REASON_TEXT = {
  [ENROLLMENT_SKIP_REASON.PROGRAM_NOT_CONFIGURED]: "Not set up for automatic enrollment yet — enroll this one manually",
  [ENROLLMENT_SKIP_REASON.DRUG_NOT_SUPPORTED]: "Automatic enrollment doesn't cover this drug for this program",
  [ENROLLMENT_SKIP_REASON.HAS_ACTIVE_APPLICATION]: "Already has an application in progress",
  [ENROLLMENT_SKIP_REASON.NAVIGATOR_DECLINED]: "Declined by a navigator earlier in this run"
};

/** Neither of these is work — they must not sit in the same pile as the ones a person must do. */
const NO_ACTION_SKIPS = [ENROLLMENT_SKIP_REASON.HAS_ACTIVE_APPLICATION, ENROLLMENT_SKIP_REASON.NAVIGATOR_DECLINED];
const isNoActionSkip = (reason) => NO_ACTION_SKIPS.includes(reason);

const HANDBACK_CAUSE = {
  [RUN_FINAL_OUTCOME.CONSENT_SENT_BY_NAVIGATOR]: "the patient didn't sign the consent in time",
  [RUN_FINAL_OUTCOME.CONSENT_OBTAINED_HANDOFF]:
    "consent is in hand, but there was no program the agent could enroll into",
  [RUN_FINAL_OUTCOME.NOTHING_ENROLLABLE]:
    "consent is in hand, but no eligible program is set up for automatic enrollment",
  [RUN_FINAL_OUTCOME.ENROLLMENT_NONE_SUBMITTED]: "consent is in hand, but none of the enrollments went through",
  [RUN_FINAL_OUTCOME.FN_CONSENT_SENDING_DECLINED]: "the consent send was declined",
  [RUN_FINAL_OUTCOME.ENROLLMENT_PARTIAL]: "the programs that didn't enroll still need a person",
  [RUN_FINAL_OUTCOME.FAILURE]: "the agent hit a technical error and couldn't finish"
};

// True when an awaiting consent-decide outcome means "eligible, ready to send"
// — the only state where the banner shows the "Will send via …" destination.
// The consent-decide `human_review` step carries the consent state itself.
const isReadyToSendOutcome = (outcome) => outcome === CONSENT_REASON.ELIGIBLE_TO_SEND;

// Some reasons benefit from per-instance detail pulled from the step's
// payload — e.g. `eligible_to_send` names the channels we can send on.
// Falls back to the static `friendlyReason` map when the payload doesn't
// carry the supplementary detail.
//
// `suppressMissingContactInfo` (set when folding the consent state onto the
// consent-DECISION step) drops any "what contact info is missing" framing:
// the navigator fills missing dob / email / phone right in the approval modal,
// so calling it out as "missing" at decision time is stale. Other surfaces
// (a standalone consent step on a no-decision run) keep the detail.
// The row that carries the pre-decision consent STATE + its context fields
// (`availableContactChannels` / `missingContactFields`). Since MVP-31712 the
// platform writes NO standalone `consent_state_check` row at all — the open
// `human_review` pause row carries the state and the same three fields. Older
// runs still have the standalone check row, so both shapes are accepted.
const carriesConsentState = (step) =>
  step.stepName === AGENTIC_STEP_NAMES.CONSENT_STATE_CHECK || isOpenConsentReview(step);

const friendlyReasonForStep = (step, { suppressMissingContactInfo = false } = {}) => {
  // A DECIDED consent-decide `human_review` step carries the pre-decision
  // consent STATE on its `outcome` (e.g. `eligible_to_send` → "Patient is ready
  // to receive a consent link."). Once the navigator has decided, that state is
  // stale history — the useful body line is the decision itself ("Declined by X
  // — Reason: …" / "Approved by X" from `stepSummary`). Suppress the state
  // sentence here so the decided step leads with the decision, not the
  // superseded "ready to send" line. The still-pending step (result == null)
  // keeps the state line as live context while it awaits a decision.
  if (
    step.stepName === AGENTIC_STEP_NAMES.CONSENT_HUMAN_REVIEW &&
    step.result != null &&
    (step.payload?.decision === REVIEW_DECISION.APPROVE || step.payload?.decision === REVIEW_DECISION.DECLINE)
  ) {
    return null;
  }
  if (carriesConsentState(step) && step.outcome === CONSENT_REASON.CONTACT_INFO_MISSING) {
    // On the decision fold there's nothing to surface — the navigator fills the
    // missing contact info at approval. Elsewhere, list the actual fields.
    if (suppressMissingContactInfo) return null;
    const fields = step.payload?.missingContactFields;
    if (Array.isArray(fields) && fields.length > 0) {
      return `Missing: ${fields.map(humanizeContactField).join(", ")}`;
    }
  }
  if (carriesConsentState(step) && step.outcome === CONSENT_REASON.ELIGIBLE_TO_SEND) {
    // Compose two halves: "what we can do" (available channels) + a
    // postscript naming any *missing* channel so navigators get the
    // full picture inline. A patient with
    // email but no phone reads:
    //   "Can send via email — phone number is missing."
    // A fully-equipped patient reads:
    //   "Can send via email or phone number."
    // Falls back to the static friendly sentence if either list is
    // unavailable (older runs without payload).
    const channels = step.payload?.availableContactChannels;
    const missing = step.payload?.missingContactFields;
    if (Array.isArray(channels) && channels.length > 0) {
      const available = channels.map(humanizeContactField).join(" or ");
      // Phone/email are the channel-bearing fields; dob is not a
      // delivery channel so it's excluded from the postscript even if
      // it's in missingContactFields (an eligible-to-send run cannot
      // be missing dob — the gate would have blocked it). On the decision
      // fold the postscript is dropped entirely (navigator fills it at approval).
      const missingChannels =
        !suppressMissingContactInfo && Array.isArray(missing)
          ? missing
              .filter((f) => f === CONTACT_FIELD.EMAIL || f === CONTACT_FIELD.PHONE_NUMBER)
              .map(humanizeContactField)
          : [];
      const suffix = missingChannels.length > 0 ? ` — ${missingChannels.join(" and ")} is missing.` : ".";
      return `Can send via ${available}${suffix}`;
    }
  }
  return friendlyReason(step.outcome);
};

// Camel-case payload field names mapped to user-facing labels. Used
// inside reason sentences (e.g. "phoneNumber" → "phone number") so the
// navigator-facing text reads naturally instead of leaking the JSON
// field identifier.
const CONTACT_FIELD_LABEL = {
  [CONTACT_FIELD.EMAIL]: "email",
  [CONTACT_FIELD.PHONE_NUMBER]: "phone number",
  [CONTACT_FIELD.DOB]: "date of birth"
};

const humanizeContactField = (field) => CONTACT_FIELD_LABEL[field] || field;

// User-name formatter for step payloads that include a hydrated `user`
// object (FN-side `hydrateAuditStepUsers` populates this from the raw
// userId). Returns null when hydration didn't land — caller decides how
// to gracefully degrade (e.g. show just the verb).
const formatUserName = (user) => {
  if (!user) return null;
  const fullName = [user.firstName, user.lastName].filter(Boolean).join(" ");
  return fullName || user.username || user.email || null;
};

// ─── Smart per-step summary lines ─────────────────────────────────────────
// Each step name renders a short, human-readable summary line. Falls back to
// the step's `reason` for steps with no dedicated formatter, or to "—" for
// fully empty rows.

// `submission` is the enrollment's folded `ee_submit` step (see the
// EE_HUMAN_REVIEW branch); every other step ignores it. `t` follows the same
// convention as `stepVerdict`: threaded in so new copy can be translated,
// while the older hardcoded lines stay as they are until they're touched.
const stepSummary = (step, submission, t, consentSend = null, patientStatuses = []) => {
  const p = step.payload || {};
  // A crashed step — the workflow's outer catch writes a FAILED step with a
  // coarse `error` and no outcome. Surface that directly instead of the stage's
  // success-shaped summary (which would otherwise read e.g. "0 of 0 reasons").
  if (step.result === AUDIT_STEP_RESULT.FAILED && !step.outcome && p.error) {
    // Crash-written step (`payload.error` is the unwrapped technical cause —
    // e.g. an FN 403). That detail is for ops (worker logs + the dashboard's
    // failed panel); navigators get a plain sentence, not an axios message.
    return "The agent hit a technical error on this step.";
  }
  switch (step.stepName) {
    case AGENTIC_STEP_NAMES.EMR_DATA_PARSED: {
      // EMR payload ships the medications array + an insurances array
      // (payer + plan name + categorical market per row). Counts come
      // from `.length`. The summary's market chunk shows the deduped
      // categorical labels (e.g. "Commercial", "Medicare") so it stays
      // short — the full payer/plan detail is in the EMR breakdown below.
      const meds = Array.isArray(p.medications) ? p.medications.length : 0;
      const insurances = Array.isArray(p.insurances) ? p.insurances : [];
      const markets = [...new Set(insurances.map((i) => i.planMarket).filter(Boolean))];
      const marketStr = markets.length ? ` (${markets.join(", ")})` : "";
      return `${meds} med${meds === 1 ? "" : "s"} · ${insurances.length} plan${
        insurances.length === 1 ? "" : "s"
      }${marketStr}`;
    }
    case AGENTIC_STEP_NAMES.FINANCIAL_ASSISTANCE_ELIGIBILITY: {
      // Reason-driven: headline the per-reason verdict count. The per-reason
      // breakdown — each reason's criteria checklist with its drug / PAP
      // evidence nested in — renders inline in the step card below
      // (ReasonChecksList).
      const reasonChecks = p.reasonChecks;
      if (Array.isArray(reasonChecks) && reasonChecks.length > 0) {
        const eligible = reasonChecks.filter((x) => x.eligible).length;
        return `${eligible} of ${reasonChecks.length} reason${reasonChecks.length === 1 ? "" : "s"} eligible`;
      }
      // No reasons evaluated (no insurance data / no reasons) — the friendly
      // reason sentence below carries the explanation.
      return "";
    }
    case AGENTIC_STEP_NAMES.INELIGIBLE_ROWS_COMPLETED: {
      const closed = Array.isArray(p.closedRows) ? p.closedRows : [];
      if (closed.length === 0) return "";
      // Agent-as-subject sentence, matching the hypercare_handoff copy.
      const one = closed.length === 1;
      return `The agent closed ${closed.length} program${
        one ? "" : "s"
      } in hypercare — the patient isn't eligible for ${one ? "it" : "them"}.`;
    }
    case AGENTIC_STEP_NAMES.REMAINING_OOP_CALCULATION: {
      const remainingOops = Array.isArray(p.remainingOops) ? p.remainingOops : [];
      const threshold = p.threshold;
      const known = remainingOops.filter((v) => v != null);
      // No coverage figures — FN had nothing to compute against, so the step
      // did nothing meaningful: read it as skipped (the row icon goes grey too).
      if (known.length === 0) {
        return "N/A — skipped (no coverage data)";
      }
      const remainingText = known.map((v) => `$${v}`).join(", ");
      return threshold != null
        ? `Remaining OOP ${remainingText} · Threshold $${threshold}`
        : `Remaining OOP ${remainingText}`;
    }
    case AGENTIC_STEP_NAMES.CONSENT_STATE_CHECK:
      // Consent state ("Ready to send" / "Active consent" / "Link in progress"
      // / "Blocked") is shown via the verdict pill, and the friendly sentence
      // is rendered below — leaving the raw reason code out of the user-facing
      // summary entirely.
      return "";
    case AGENTIC_STEP_NAMES.ASSIGN_PATIENT_TO_AGENT: {
      // Named as the TRANSITION it caused — this row is the only place the old
      // status survives. Labels come from the site's `statuses`; a value it does
      // not list falls back to the stored value, like the row below.
      if (!p.status) return "";
      const label = (value) => patientStatuses?.find((status) => status.value === value)?.text || value;
      const to = label(p.status);
      const from = p.previousStatus ? label(p.previousStatus) : null;
      const statusClause = from
        ? `Set the patient's status from "${from}" to "${to}"`
        : `Set the patient's status to "${to}"`;
      return p.assigneeAdded ? `${statusClause} and added the TM assistant as an assignee.` : `${statusClause}.`;
    }
    case AGENTIC_STEP_NAMES.UPDATE_PATIENT_STATUS: {
      // The "resolve the patient" side-effect: the agent writes Patient.status
      // (and, on the consent/oop-met paths, a follow-up date). Spell out the
      // transition as a sentence so a navigator sees which status the patient
      // landed in — and what the step did — without expanding Details.
      // Not-eligible terminals set status only, so the follow-up clause is
      // dropped. (The step keeps the `update_patient_status` name even though it
      // also carries the follow-up date — see the platform AgenticStepName.)
      if (!p.status) return "";
      const followUp = p.followupDate
        ? ` and scheduled a follow-up for ${moment(p.followupDate).local().format("MMM D, YYYY")}`
        : "";
      return `Set the patient's status to "${p.status}"${followUp}.`;
    }
    case AGENTIC_STEP_NAMES.ENROLLMENT_CANDIDATES: {
      // The count line. WHICH programs, and who has to act on each, is the
      // grouped list rendered below this row — a count alone just prompts
      // "which ones?". Consent is worth having either way, so this row is
      // deliberately informational and never framed as a shortfall.
      //
      // No "never evaluated" case here: the platform only writes this step
      // once it has at least one program to name, so the row's presence
      // means there is a list under it.
      const eligible = p.eligibleCount ?? 0;
      const enrollable = p.enrollableCount ?? 0;
      if (!eligible) return "";
      const programWord = eligible === 1 ? "program" : "programs";
      if (!enrollable) {
        return `${eligible} ${programWord} to enroll in — none can be enrolled automatically, so they all need a person.`;
      }
      if (enrollable === eligible) {
        return `${eligible} ${programWord} to enroll in — the agent can enroll ${
          eligible === 1 ? "it" : "all of them"
        }.`;
      }
      return `${eligible} ${programWord} to enroll in — the agent can enroll ${enrollable}; the rest need a person.`;
    }
    // The closing row: the agent is done, and this says what it left for a person.
    //
    // WHICH program went which way is the `enrollment_candidates` row above —
    // named, one line each. Repeating that here (as two rows, one per side) made
    // the reader recombine them to answer the only question this row exists for,
    // so it deliberately talks about the CASE, never about programs or row counts.
    // A run is always ONE patient, so the row ids stay an internal detail.
    case AGENTIC_STEP_NAMES.HYPERCARE_HANDOFF: {
      if (step.outcome === HYPERCARE_HANDOFF_OUTCOME.COMPLETED) {
        return t("agentic_runs.hypercare_handoff.completed");
      }
      // A handback with no reason is the least useful row in the trail: whoever
      // picks the patient up next needs to know how far the agent got and why it
      // stopped. The platform stamps the run's ending (and its detail when the
      // ending carried one) onto this step for exactly that. It stays useful on
      // endings with no program list above — declined, never signed.
      //
      // Deliberately NOT `friendlyReason` — those sentences are written to stand
      // alone and each already ends with "the patient is back on the queue", so
      // splicing one in here says the same thing twice. HANDBACK_CAUSE holds the
      // clause form instead.
      const cause = HANDBACK_CAUSE[p.finalOutcome] || p.detail;
      // "its part" only when the agent actually closed something on its way —
      // always read from THIS row's own payload, never from a sibling step.
      const key = p.completedRowIds?.length ? "partial" : "handed_back";
      return cause
        ? t(`agentic_runs.hypercare_handoff.${key}_because`, { cause: lowerFirst(cause) })
        : t(`agentic_runs.hypercare_handoff.${key}`);
    }
    case AGENTIC_STEP_NAMES.CONSENT_SIGNATURE_WAIT: {
      // The window between the send and either a signature or the deadline. Its
      // `detectedVia` says HOW the signature was learned: `signal` (FN's
      // consent-accepted notification, the fast path) or `poll` (a slice of the
      // wait re-read the consent state and found it active — the notification
      // was lost). Navigators don't care which mechanism fired, so that stays
      // out of the sentence; what they need is whether the patient signed.
      if (step.result == null) {
        return `The consent link is out — waiting${
          p.ttl ? ` up to ${p.ttl}` : ""
        } for the patient to sign it. Nothing for you to do yet.`;
      }
      if (step.outcome === CONSENT_SIGNATURE_WAIT_OUTCOME.SIGNED) {
        return p.detectedVia === CONSENT_SIGNATURE_DETECTION.POLL
          ? "The patient signed the consent — the agent picked it up on a routine re-check and is carrying on."
          : "The patient signed the consent — the agent is carrying on.";
      }
      if (step.outcome === CONSENT_SIGNATURE_WAIT_OUTCOME.NOT_SIGNED) {
        return `The patient didn't sign${
          p.ttl ? ` within ${p.ttl}` : ""
        } — the agent stopped here and handed them back to you.`;
      }
      return "";
    }
    case AGENTIC_STEP_NAMES.EMR_MANUAL_FILL: {
      // A FILL, not a decision — the navigator entered the EMR data, they didn't
      // approve a recommendation. So its attribution reads "Filled by X" (no
      // Approved/Declined verb, no verdict pill — see stepVerdict), and the
      // entered insurances/meds block renders below it.
      //
      // Still-open pause (`result == null`): nothing's been filled yet — the
      // run is awaiting the navigator. Spell out what happened + what to do so
      // the step explains itself, rather than leaving an empty body under the
      // "Waiting for navigator" chip (mirrors the screenshots-wait copy).
      if (step.result == null) {
        return "The agent couldn't gather the patient's EMR data automatically — waiting for a navigator to enter it manually in the Tasks page.";
      }
      // A resolved pause is a fill ONLY when a navigator actually approved
      // (entered data). A timeout/decline resolved the pause with NOTHING
      // filled — saying "Filled" here misreports what happened.
      if (step.result === AUDIT_STEP_RESULT.TIMED_OUT) {
        return `No one filled the EMR data within the wait window${
          p.ttl ? ` (${p.ttl})` : ""
        } — the patient was returned to the queue.`;
      }
      if (p.decision === REVIEW_DECISION.DECLINE) {
        const decliner = formatUserName(p.user);
        return decliner ? `Manual fill declined by ${decliner}.` : "Manual fill declined.";
      }
      const filler = formatUserName(p.user);
      return filler
        ? `EMR data entered manually by ${filler} — the agent resumed with it.`
        : "EMR data entered manually — the agent resumed with it.";
    }
    case AGENTIC_STEP_NAMES.CONSENT_HUMAN_REVIEW: {
      // The decision (approve / decline) rides on `payload.decision`, NOT on
      // `step.outcome` — for a consent_decide pause `step.outcome` is the
      // consent STATE (e.g. `eligible_to_send`), so keying the verb off
      // `outcome` always missed and this attribution line never rendered.
      // Read the decision field directly.
      const verb =
        p.decision === REVIEW_DECISION.APPROVE
          ? "Approved"
          : p.decision === REVIEW_DECISION.DECLINE
          ? "Declined"
          : null;
      if (!verb) return "";
      // FN-side `hydrateAuditStepUsers` populates `payload.user`
      // ({ firstName, lastName, ... }) from `payload.userId` so we can name the
      // navigator who decided without a second round-trip — the SAME path that
      // renders "Sent by X" on the send_consent step below. Falls back
      // gracefully when hydration didn't land (older row, user record gone, an
      // id UMS no longer knows): we show the bare verb, never "by undefined" or
      // the raw Okta id.
      const name = formatUserName(p.user);
      // The send is FOLDED in here, exactly as `ee_submit` folds onto the
      // enrollment decision below: approving and dispatching the link are a
      // single act to a navigator, and splitting them made a two-row story out
      // of one ("Approved by X" / "Sent via email by X" — same person, same
      // second). Only a SUCCESSFUL send folds; a blocked or failed dispatch
      // keeps its own row, because then the two really are different events.
      const sentChannels =
        verb === "Approved" && Array.isArray(consentSend?.payload?.channels)
          ? consentSend.payload.channels.join(", ")
          : "";
      const act = sentChannels ? `Approved and sent via ${sentChannels}` : verb;
      const head = name ? `${act} by ${name}` : act;
      // Decline-only: the navigator's free-text reason rides on the payload
      // (workflow writes it when the decline signal carries one). It used to be
      // jammed into the verdict pill ("Declined — <reason>"); it now lives here
      // in the body — the only place arbitrary navigator prose belongs — so the
      // line reads "Declined by X — Reason: Y" (or just "Declined — Reason: Y"
      // when the name couldn't be resolved). React escapes the free text.
      if (p.decision === REVIEW_DECISION.DECLINE && p.declineReason && p.declineReason.trim()) {
        return `${head} — Reason: ${p.declineReason.trim()}`;
      }
      return head;
    }
    case AGENTIC_STEP_NAMES.EE_HUMAN_REVIEW: {
      // The enrollment as ONE event: what the navigator decided, and what became
      // of it. The `ee_submit` step is FOLDED in here rather than given a row of
      // its own — "approved" and "submitted" are a single act to a navigator, and
      // splitting them made a two-row story out of one. When the vendor call
      // failed, that stays on this same line (and re-colors the row): the
      // application and its filled form survive, so someone can finish it by hand.
      //
      // Derived from `result` + `payload.decision`, never from which steps exist:
      // a declined review, a timed-out one and an approval whose submit hasn't
      // landed yet all leave no `ee_submit` behind, and they are three different
      // stories. Attribution reuses `formatUserName` — the same hydrated
      // `payload.user` the consent decision names its navigator from — and drops
      // to the unattributed wording when hydration didn't land, never "undefined".
      if (step.result == null) {
        // Still parked on the navigator. The "Waiting for navigator" chip is the
        // whole message; a summary line would only repeat it.
        return "";
      }
      if (step.result === AUDIT_STEP_RESULT.TIMED_OUT) {
        return t("agentic_runs.enrollment.timed_out");
      }
      const name = formatUserName(p.user);
      const attributed = (key) => t(name ? key : `${key}_unattributed`, { name });
      if (p.decision === REVIEW_DECISION.DECLINE) {
        const declined = attributed("agentic_runs.enrollment.declined");
        return p.declineReason && p.declineReason.trim()
          ? t("agentic_runs.enrollment.reason", { decision: declined, reason: p.declineReason.trim() })
          : declined;
      }
      if (p.decision === REVIEW_DECISION.APPROVE) {
        // No submission step at all: FN reported unresolved fields, so the
        // workflow ended on the approval without ever calling the vendor.
        if (!submission) return attributed("agentic_runs.enrollment.approved");
        return submission.result === AUDIT_STEP_RESULT.FAILED
          ? attributed("agentic_runs.enrollment.submit_failed")
          : attributed("agentic_runs.enrollment.submitted");
      }
      // Resolved with no decision on it — the child crashed while parked. The
      // red row carries that; there's no decision to report.
      return "";
    }
    case AGENTIC_STEP_NAMES.SEND_CONSENT: {
      // outcome='sent' is the success case; render channels + sender.
      // Failure outcomes (consent reasons like contact_info_missing,
      // patient_has_active_consent, etc.) carry their own friendlyReason
      // text from REASON_FRIENDLY_TEXT below the step, so we render
      // nothing extra here on those branches.
      if (step.outcome !== SEND_CONSENT_OUTCOME.SENT) return "";
      const channels = Array.isArray(p.channels) ? p.channels.join(", ") : "";
      const name = formatUserName(p.user);
      if (channels && name) return `Sent via ${channels} by ${name}`;
      if (channels) return `Sent via ${channels}`;
      if (name) return `Sent by ${name}`;
      return "Sent";
    }
    default:
      // Unknown / unmapped step → render nothing rather than the raw
      // reason code.
      return "";
  }
};

// ─── Components ───────────────────────────────────────────────────────────

const StatusChip = ({ visual }) => {
  const Icon = visual.icon;
  return (
    <div
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 4,
        background: visual.bg,
        color: visual.fg,
        padding: "2px 8px",
        borderRadius: 12,
        fontSize: 11,
        fontWeight: 600
      }}
    >
      <Icon spin={!!visual.running} />
      <span>{visual.label}</span>
    </div>
  );
};

// One drug / PAP leaf under a criterion — the deepest level of the
// financial-assistance breakdown. No icon: the criterion above carries the
// verdict, so a leaf just shows the drug / program name (+ a reason if failed).
// Caption above a partitioned sub-list — the EMR breakdown's Insurances /
// Medications headings, the enrollment-candidate group titles, and the
// Eligible / Not-eligible split below. Four sites had each grown their own inline
// copy, and one of them had drifted to a different weight by accident.
const groupLabelStyle = (color = GRAY_500) => ({
  color,
  fontSize: 11,
  fontWeight: 600,
  display: "block"
});

const CriterionItemRow = ({ item }) => {
  // Every candidate is listed now — the eligible ones and the ruled-out ones — so
  // each item states its own verdict.
  //
  // A ruled-out one gets the GREY MINUS, never the red ✗: a program the patient
  // doesn't qualify for is an informational verdict, not an error (the same call
  // this file already makes for the step's own "Not eligible" pill). These lists
  // routinely run several deep, so red here would fill the panel with alarm over
  // a run where nothing went wrong.
  const visual = item.passed ? STEP_RESULT_VISUAL.passed : STEP_RESULT_VISUAL.skipped;
  const Icon = visual.icon;
  return (
    <div style={{ marginTop: 4, display: "flex", alignItems: "flex-start", gap: 8 }}>
      <Icon
        style={{
          color: item.passed ? SUCCESS_500 : GRAY_500,
          fontSize: 11,
          flexShrink: 0,
          marginTop: 3
        }}
      />
      <div>
        <Text style={{ color: GRAY_800, fontSize: 12, display: "block" }}>{item.label}</Text>
        {/* Shown on EITHER verdict: a reader asks "why this one?" as readily */}
        {/* as "why not that one?". */}
        {item.detail && (
          <Text style={{ color: GRAY_500, fontSize: 11, display: "block", marginTop: 2 }}>{item.detail}</Text>
        )}
      </div>
    </div>
  );
};

// Candidate items split by verdict, headed and collapsed only when BOTH sides
// exist. A criterion whose items all landed the same way needs no heading to
// explain itself — and when none passed, those items ARE the explanation of the
// failure, so folding them away would hide the answer.
const CriterionItemGroups = ({ items }) => {
  const eligible = items.filter((it) => it.passed);
  const ruledOut = items.filter((it) => !it.passed);
  const split = eligible.length > 0 && ruledOut.length > 0;
  const [showRuledOut, setShowRuledOut] = useState(false);
  const [hovered, setHovered] = useState(false);

  return (
    <div style={{ marginLeft: 20, marginTop: 2 }}>
      {eligible.length > 0 && (
        <div>
          {split && <Text style={{ ...groupLabelStyle(), marginTop: 2 }}>Eligible</Text>}
          {eligible.map((it, i) => (
            <CriterionItemRow key={`${it.label}-${i}`} item={it} />
          ))}
        </div>
      )}
      {ruledOut.length > 0 && (
        <div style={{ marginTop: split ? 8 : 0 }}>
          {/* Native <button> for keyboard reach; antd's Button and Collapse both */}
          {/* bring padding a row nested this deep cannot absorb. The underline on */}
          {/* hover/focus is what gives it shape — a bare chevron and label reads */}
          {/* as an artifact rather than a control (cf. EeReviewBody's Collapse). */}
          {split && (
            <button
              type="button"
              onClick={() => setShowRuledOut((open) => !open)}
              aria-expanded={showRuledOut}
              data-testid="agentic-ruled-out-toggle"
              onMouseEnter={() => setHovered(true)}
              onMouseLeave={() => setHovered(false)}
              onFocus={() => setHovered(true)}
              onBlur={() => setHovered(false)}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 6,
                background: "none",
                border: "none",
                padding: 0,
                cursor: "pointer",
                textDecoration: hovered ? "underline" : "none",
                textDecorationColor: GRAY_500
              }}
            >
              {/* One icon rotated, matching AgenticPlatformTasks' expandIcon. */}
              <RightOutlined rotate={showRuledOut ? 90 : 0} style={{ fontSize: 9, color: GRAY_500 }} />
              <Text style={groupLabelStyle()}>{`Not eligible (${ruledOut.length})`}</Text>
            </button>
          )}
          {(!split || showRuledOut) && ruledOut.map((it, i) => <CriterionItemRow key={`${it.label}-${i}`} item={it} />)}
        </div>
      )}
    </div>
  );
};

const CriterionRow = ({ criterion }) => {
  // A PASSED criterion is a checklist item the reason met → green check + label.
  // A FAILED criterion shows its `detail` (the why) as a plain line, no icon:
  // the reason row above already carries the ✗, so an icon on the sentence (and
  // the label, which would just restate the detail) is noise.
  const PassIcon = STEP_RESULT_VISUAL.passed.icon;
  const label = !criterion.passed && criterion.detail ? criterion.detail : criterion.label;
  const items = Array.isArray(criterion.items) ? criterion.items : [];
  // The one-glance answer, so the reader does not have to count the rows below.
  // Only worth saying when there was actually a choice to make.
  const tally = items.length > 1 ? ` · ${items.filter((it) => it.passed).length} of ${items.length} eligible` : "";
  return (
    <div style={{ marginTop: 6 }}>
      <div style={{ display: "flex", alignItems: "flex-start", gap: 8 }}>
        {criterion.passed && <PassIcon style={{ color: SUCCESS_500, fontSize: 11, flexShrink: 0, marginTop: 3 }} />}
        <Text style={{ color: GRAY_800, fontSize: 12 }}>
          {label}
          {tally}
        </Text>
      </div>
      {items.length > 0 && <CriterionItemGroups items={items} />}
    </div>
  );
};

// Per-reason eligibility verdicts — rendered inside the financial-assistance
// step card from `payload.reasonChecks`. Each reason shows a pass/fail icon and,
// beneath it, the criteria that explain its verdict: an ELIGIBLE reason shows the
// criteria it met (why eligible); a NOT-ELIGIBLE reason shows only the criteria
// it failed (why not). Criterion detail renders on failure only. Any eligible → run advances.
const ReasonChecksList = ({ checks }) => (
  <div style={{ marginTop: 10 }}>
    {checks.map((c, idx) => {
      const visual = c.eligible ? STEP_RESULT_VISUAL.passed : STEP_RESULT_VISUAL.failed;
      const Icon = visual.icon;
      return (
        <div
          key={`${c.reason}-${idx}`}
          style={{
            padding: "8px 0",
            borderTop: idx === 0 ? "none" : `1px dashed ${GRAY_200}`
          }}
        >
          <div style={{ display: "flex", alignItems: "flex-start", gap: 8 }}>
            <Icon style={{ color: c.eligible ? SUCCESS_500 : visual.fg, fontSize: 12, flexShrink: 0, marginTop: 3 }} />
            <Text style={{ color: GRAY_800, fontSize: 12, fontWeight: 500 }}>{hypercareReasonLabel(c.reason)}</Text>
          </div>
          <div style={{ marginLeft: 20 }}>
            {(c.criteria || [])
              .filter((cr) => (c.eligible ? cr.passed : !cr.passed))
              .map((cr, i) => (
                <CriterionRow key={`${cr.label}-${i}`} criterion={cr} />
              ))}
          </div>
        </div>
      );
    })}
  </div>
);

// papId -> the program label the eligibility step recorded for it. That step
// evaluates EVERY candidate pap and stores each one's name and verdict, so the
// closed-rows row below can name what it closed without the run persisting the
// same program string on a second step.
const getPapLabelsFromEligibility = (steps) => {
  const labels = new Map();
  const step = (steps || []).find((s) => s.stepName === AGENTIC_STEP_NAMES.FINANCIAL_ASSISTANCE_ELIGIBILITY);
  const checks = step?.payload?.reasonChecks;
  (Array.isArray(checks) ? checks : []).forEach((c) =>
    (c.criteria || []).forEach((cr) =>
      (cr.items || []).forEach((it) => {
        if (it.papId != null && it.label) labels.set(it.papId, it.label);
      })
    )
  );
  return labels;
};

// Which programs the agent closed at discovery, and why. Grey markers, not red:
// these rows are RESOLVED work — the agent answered their question and took them
// off the board — rather than anything that went wrong.
const ClosedProgramsList = ({ closedRows, resolutionReason, papLabels }) => (
  <div data-testid="agentic-closed-programs" style={{ marginTop: 10 }}>
    {closedRows.map(({ rowId, papId }) => (
      // A closed row IS a ruled-out candidate, so it renders as one: `passed:
      // false` gives the grey marker, and no `detail` leaves the row a single
      // line. Sharing the row keeps the two lists from drifting apart the next
      // time either one's spacing or marker changes.
      <CriterionItemRow
        key={rowId}
        item={{
          label: papLabels?.get(papId) || (papId != null ? `Program ${papId}` : `Queue row ${rowId}`),
          passed: false
        }}
      />
    ))}
    {resolutionReason && (
      <Text style={{ color: GRAY_500, fontSize: 11, display: "block", marginTop: 6 }}>
        Hypercare resolution: {resolutionReason}
      </Text>
    )}
  </div>
);

// ─── EMR data, in plain language ──────────────────────────────────────────
// The `emr` step's payload carries the structured EMR the workflow loaded —
// `insurances` ({ planName, planType, planMarket, financialClass, groupName,
// group }) + `medications` ({ name, firstTreatmentDate?, lastTreatmentDate?,
// rxnormDrug? }). The same
// payload backs both paths: the screenshot path (VLM-extracted) and the manual
// path (navigator-entered via the emr_fill HITL task, then loaded by the same
// step). These formatters turn each row into one navigator-readable line — no
// raw JSON — so a non-technical TMC navigator sees what coverage + meds the
// agent worked from.

// One insurance row → "<planName> — <market>", with the categorical market
// humanized via the SAME `agentic_board.emr_fill.plan_market_options` labels
// the EMR-fill picker + the consent modal's steps-summary use (so the three
// surfaces read identically; unmapped codes fall back to the raw value). The
// plan type and group are appended only when present, each behind a readable
// label, so a bare-bones row stays a clean single clause and a rich row reads
// "Humana — Commercial · Type: PPO · Group: ACME (12345)".
const formatInsuranceLine = (insurance, t) => {
  const market = insurance.planMarket ? t(`agentic_board.emr_fill.plan_market_options.${insurance.planMarket}`) : null;
  // Lead with the plan name; fall back to the market alone when the row has no
  // name (never render an empty lead or a dangling em dash).
  const head = [insurance.planName, market].filter(Boolean).join(" — ");
  const extras = [];
  if (insurance.planType) extras.push(t("agentic_runs.emr_data.plan_type", { value: insurance.planType }));
  // Prefer the human group name; show the group id in brackets when both exist
  // ("ACME (12345)"), or whichever one is present on its own.
  if (insurance.groupName || insurance.group) {
    const groupLabel =
      insurance.groupName && insurance.group
        ? `${insurance.groupName} (${insurance.group})`
        : insurance.groupName || insurance.group;
    extras.push(t("agentic_runs.emr_data.group", { value: groupLabel }));
  }
  const tail = extras.length > 0 ? ` · ${extras.join(" · ")}` : "";
  return `${head || market || ""}${tail}`;
};

// True when a payload actually carries EMR data worth rendering — at least one
// insurance or named medication. Gates the breakdown so an `emr` step with an
// empty/absent payload (e.g. a legacy row) renders no breakdown rather than an
// empty block.
const hasEmrData = (payload) => {
  const insurances = Array.isArray(payload?.insurances) ? payload.insurances : [];
  const medications = Array.isArray(payload?.medications) ? payload.medications.filter((m) => m?.name) : [];
  return insurances.length > 0 || medications.length > 0;
};

// Readable insurances + medications breakdown rendered in place of the raw-JSON
// fold for EMR data. Two labeled sub-lists, one line per row. Renders nothing
// when the payload carries neither (defensive — the caller only mounts this via
// `hasEmrData`). `testIdPrefix` scopes the data-testids so the manual-fill block
// and the screenshot `emr` block stay independently queryable.
const EmrDataBreakdown = ({ payload, testIdPrefix, t }) => {
  const insurances = Array.isArray(payload?.insurances) ? payload.insurances : [];
  const medications = Array.isArray(payload?.medications) ? payload.medications.filter((m) => m?.name) : [];
  if (insurances.length === 0 && medications.length === 0) return null;

  return (
    <div data-testid={`${testIdPrefix}-emr-data`} style={{ marginTop: 10 }}>
      {insurances.length > 0 && (
        <div>
          <Text style={groupLabelStyle()}>{t("agentic_runs.emr_data.insurances")}</Text>
          {insurances.map((insurance, idx) => (
            <Text
              key={`ins-${idx}`}
              data-testid={`${testIdPrefix}-emr-insurance`}
              style={{ color: GRAY_800, fontSize: 12, display: "block", marginTop: 2 }}
            >
              {formatInsuranceLine(insurance, t)}
            </Text>
          ))}
        </div>
      )}
      {medications.length > 0 && (
        <div style={{ marginTop: insurances.length > 0 ? 8 : 0 }}>
          <Text style={groupLabelStyle()}>{t("agentic_runs.emr_data.medications")}</Text>
          {medications.map((med, idx) => (
            <div key={`med-${idx}`} style={{ marginTop: 2 }}>
              <Text
                data-testid={`${testIdPrefix}-emr-medication`}
                style={{ color: GRAY_800, fontSize: 12, display: "block" }}
              >
                {formatMedicationLine(med, t)}
                {/* Strict null = the platform tried and failed to map the name to FN's
                    RxNorm catalog (so PAP/active-med matching can't fire). Absent (legacy
                    rows that predate rxnormDrugId) = unknown, so we don't flag it. Rendered
                    INLINE after the drug name so it's unambiguous WHICH med didn't map. */}
                {med.rxnormDrugId === null && (
                  <Text
                    data-testid={`${testIdPrefix}-emr-medication-unmatched`}
                    style={{ color: WARNING_500, fontSize: 11, marginLeft: 6, whiteSpace: "nowrap" }}
                  >
                    <ExclamationCircleFilled style={{ marginRight: 4 }} />
                    {t("agentic_runs.emr_data.medication_not_matched")}
                  </Text>
                )}
              </Text>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// Display names for known step types. Step codes (snake_case) are stable
// strings the platform writes for analytics + queries; the human label
// is FE-only. Unknown step codes fall through to a simple humanize.
const STEP_DISPLAY_NAME = {
  // Screenshot collection phase: workflow waits while operators upload
  // EMR screenshots on the Agentic Platform → EMR Tasks tab. Writes
  // with `result: null` during the wait (modal renders "Running").
  [AGENTIC_STEP_NAMES.EMR_GATHERING]: "EMR Data Gathering",
  // Downstream audit step — what the agent parsed out of the EMR data
  // once gathering has produced something usable. Distinct from the
  // gathering wait above; renamed from "EMR Data Gathering" to avoid
  // collision now that `emr_gathering` carries that meaning.
  //
  // Named for WHAT the step produces, not where it came from: on a site with
  // EMR data turned off this same step reads the patient's TM record, and
  // "EMR Data Parsed" would then be describing a source that wasn't used.
  [AGENTIC_STEP_NAMES.EMR_DATA_PARSED]: "Collect Patient Data",
  [AGENTIC_STEP_NAMES.FINANCIAL_ASSISTANCE_ELIGIBILITY]: "Financial Assistance Eligibility Validation",
  [AGENTIC_STEP_NAMES.REMAINING_OOP_CALCULATION]: "Remaining OOP",
  [AGENTIC_STEP_NAMES.CONSENT_STATE_CHECK]: "Consent",
  [AGENTIC_STEP_NAMES.CONSENT]: "Consent",
  [AGENTIC_STEP_NAMES.SEND_CONSENT]: "Send Consent Link",
  // The STAGE, not its outcome — "…wait" in the title left a resolved row still
  // reading as waiting after the patient had signed. Signed / Not signed is the
  // verdict pill's job, exactly as on the rows either side of this one.
  [AGENTIC_STEP_NAMES.CONSENT_SIGNATURE_WAIT]: "Consent signature",
  // Named for what a navigator recognises — the programs this patient was
  // recommended for — not for the agent's internal candidate evaluation.
  [AGENTIC_STEP_NAMES.ENROLLMENT_CANDIDATES]: "Recommended enrollments",
  // Names the queue AND the unit a navigator recognises. "Row" is our internal
  // word for it — the board's own column labels are reason / state / program.
  [AGENTIC_STEP_NAMES.INELIGIBLE_ROWS_COMPLETED]: "Hypercare programs closed",
  // The agentic-platform centralized queue IS the hypercare queue — name it so.
  // One row for the whole wrap-up, so the label names the handoff, not an action.
  [AGENTIC_STEP_NAMES.HYPERCARE_HANDOFF]: "Hypercare handoff",
  // Express enrollment. Wording matches the same steps' labels in the run-audit
  // i18n block; without these they fall through the humanizer as "Ee config
  // check". Note a patient can have SEVERAL of each — one per enrollable pap —
  // so these read as the step, not the enrollment.
  [AGENTIC_STEP_NAMES.EE_CONFIG_CHECK]: "Express enrollment availability",
  // Inside a panel already headed by the program being enrolled, repeating
  // "Express enrollment decision" named the same enrollment a third time. The row
  // is the decision; the panel says which enrollment.
  [AGENTIC_STEP_NAMES.EE_HUMAN_REVIEW]: "Decision",
  [AGENTIC_STEP_NAMES.EE_SUBMIT]: "Express enrollment submission"
  // The two navigator HITL steps (consent_human_review / emr_manual_fill) are
  // titled by `humanReviewLabel` (i18n), keyed on the step name — not here.
};

const humanizeStepName = (name) => {
  if (!name) return "";
  if (STEP_DISPLAY_NAME[name]) return STEP_DISPLAY_NAME[name];
  const spaced = name.replace(/_/g, " ");
  return spaced[0].toUpperCase() + spaced.slice(1);
};

// ─── HITL step: per-task label + outcome ──────────────────────────────────
// The two navigator HITL pauses each have their own step name, so the display
// title + "what happened" line are driven by the step name directly (a "what
// happened" line is composed from `result` + `payload.decision` + `declineReason`):
//   emr_manual_fill      → "EMR data — manual fill"   (fill-only; no verdict)
//   consent_human_review → "Consent decision"
const HITL_STEP_LABEL = {
  [AGENTIC_STEP_NAMES.EMR_MANUAL_FILL]: {
    key: "agentic_runs.human_review.task.emr_fill",
    fallback: "EMR data — manual fill"
  },
  [AGENTIC_STEP_NAMES.CONSENT_HUMAN_REVIEW]: {
    key: "agentic_runs.human_review.task.consent_decide",
    fallback: "Consent decision"
  }
};

// Resolve a HITL step's display title from its step name. Only ever called for
// the two HITL step names (the caller routes by step name), so `meta` is set.
const humanReviewLabel = (step, t) => {
  const meta = HITL_STEP_LABEL[step.stepName];
  return t(meta.key, meta.fallback);
};

// Compose the verdict-pill label for a decided/timed-out/awaiting
// `human_review` step from `result` + `payload.decision`. The consent pause
// is a Send / Don't-send *decision* the navigator owns, so its pending +
// approve copy is decision-framed; the emr_fill pause is a fill, so it keeps
// the neutral navigator wording:
//   result === null              → "Awaiting your decision" (consent_decide)
//                                   / "Waiting for navigator" (emr_fill)
//   result === "timed_out"       → "Timed out"           (TTL elapsed, no act)
//   decision === "approve"       → "Approved" (the send is a separate step)
//   decision === "decline"       → "Declined"
// The free-text decline reason is intentionally NOT folded into the pill — a
// pill is a one-glance status chip, not a place for arbitrary navigator prose
// (a long reason blew the pill out / truncated mid-word). The reason + who
// declined are surfaced in the step body instead (see `stepSummary`'s
// human_review branch). Returns "" when the step carries no recognizable state
// (defensive — an older row with neither a result nor a decision renders no
// pill).
const humanReviewOutcomeText = (step, t) => {
  const { decision } = step.payload || {};
  if (step.result == null) {
    // The consent pause asks the navigator to decide Send / Don't-send, so it
    // reads "Awaiting your decision"; the manual-fill pause → "Waiting for navigator".
    return step.stepName === AGENTIC_STEP_NAMES.CONSENT_HUMAN_REVIEW
      ? t("agentic_runs.human_review.outcome.awaiting_decision")
      : t("agentic_runs.human_review.outcome.awaiting");
  }
  if (step.result === AUDIT_STEP_RESULT.TIMED_OUT) {
    return t("agentic_runs.human_review.outcome.timed_out");
  }
  if (decision === REVIEW_DECISION.DECLINE) {
    return t("agentic_runs.human_review.outcome.declined");
  }
  if (decision === REVIEW_DECISION.APPROVE) {
    // This step is ONLY the navigator's decision. The actual consent dispatch is
    // a separate, later `send_consent` step with its own "sent" verdict — so this
    // pill must read just "Approved", not "Approved & sent" (which conflated the
    // two and claimed a send that hadn't happened yet).
    return t("agentic_runs.human_review.outcome.approved");
  }
  return "";
};

// MySQL's default DATETIME stores wall-second precision, so the `.SSS`
// padding was always `.000` — visually noisy without adding info. The
// backend orders steps by `id` ASC, which is the definitive insertion-
// order signal anyway, so we don't need sub-second display to convey
// ordering. If a future migration bumps the column to DATETIME(3), the
// format can grow `.SSS` back in.
// App-wide moment default is UTC (index.js); `.local()` renders step times in
// the navigator's timezone (otherwise a 19:35 IDT run shows as 16:35).
const FULL_TIME_FORMAT = "MMM D, YYYY HH:mm:ss";
/** Day-and-time, no year — enough to place a decision within the run. */
const SHORT_TIME_FORMAT = "MMM D, HH:mm";

const formatTime = (ts, format = FULL_TIME_FORMAT) => (ts ? moment(ts).local().format(format) : "—");

/** A step's own moment within the run — the date sits on the run header. */
const STEP_TIME_FORMAT = "HH:mm:ss";

/**
 * A gap only worth naming once it's longer than the agent's own work takes.
 * Below this, consecutive steps are effectively simultaneous and a marker for
 * every one of them would be noise on the rail.
 */
const NOTABLE_GAP_MS = 5 * 60 * 1000;

/** "3h 27m" / "12m" / "2d 4h" — a span the navigator skims, not reads. */
const formatElapsed = (ms) => {
  const elapsed = moment.duration(ms);
  const days = Math.floor(elapsed.asDays());
  if (days > 0) return `${days}d ${elapsed.hours()}h`;
  if (elapsed.hours() > 0) return `${elapsed.hours()}h ${elapsed.minutes()}m`;
  return `${Math.max(elapsed.minutes(), 1)}m`;
};

/** ms between two timestamps, or 0 when either is missing. */
const gapMs = (from, to) => (from && to ? Math.max(moment(to).diff(moment(from)), 0) : 0);

/**
 * Dead time on the rail, as its own marker.
 *
 * Nearly always the agent waiting on a person. It was previously invisible —
 * inferable only by subtracting two timestamps three lines apart — which hid
 * the single most useful fact about a slow run: nothing was broken, someone
 * hadn't answered yet.
 *
 * `open` is the still-running variant: a parked step has only the event that
 * opened it, so the span runs to NOW and the marker TRAILS its row rather than
 * preceding it. Same dot, same rail geometry — one component so the two can't
 * drift apart visually.
 */
const WaitMarker = ({ ms, t, open = false }) => (
  <div
    style={{ position: "relative", padding: "5px 0" }}
    data-testid={open ? "agentic-run-open-wait-marker" : "agentic-run-wait-marker"}
  >
    <span
      aria-hidden
      style={{
        display: "inline-block",
        width: 9,
        height: 9,
        marginRight: 8,
        borderRadius: "50%",
        background: GRAY_50,
        border: `1px dashed ${GRAY_200}`
      }}
    />
    <Text style={{ fontSize: 11, color: GRAY_500 }}>
      {t(open ? "agentic_runs.waiting_so_far" : "agentic_runs.waited", { elapsed: formatElapsed(ms) })}
    </Text>
  </div>
);

/**
 * A SUPERSEDED attempt, folded to one line.
 *
 * Everything it did was re-run by the attempt below, so the detail is history
 * rather than the state of the journey — but it is not noise: it holds the
 * human work of that pass (a decision that was later re-asked, an operator's
 * upload) and the release that ended it. Folded, listed, openable.
 */
const CollapsedAttempt = ({ attempt, stepCount, open, onToggle, t }) => (
  <div style={{ borderBottom: `1px solid ${GRAY_100}` }}>
    <div
      role="button"
      tabIndex={0}
      data-testid={`agentic-run-attempt-${attempt.index}`}
      aria-expanded={open}
      onClick={onToggle}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onToggle();
        }
      }}
      style={{ display: "flex", alignItems: "center", gap: 9, padding: "8px 0", cursor: "pointer" }}
    >
      <RightOutlined rotate={open ? 90 : 0} style={{ fontSize: 11, color: GRAY_500 }} />
      <ReloadOutlined style={{ fontSize: 13, color: GRAY_500 }} />
      <Text style={{ fontSize: 13, color: GRAY_600 }}>
        {attempt.restart?.toVersion
          ? t("agentic_runs.restarted_on_version", { version: attempt.restart.toVersion })
          : t("agentic_runs.first_run")}
      </Text>
      {/* The first attempt did not re-run anything — it ran. Only an attempt a */}
      {/* restart produced gets the "re-ran" wording, matching the label above. */}
      <Text type="secondary" style={{ fontSize: 12 }}>
        {`· ${t(attempt.restart ? "agentic_runs.steps_re_ran" : "agentic_runs.attempt_step_count", {
          count: stepCount
        })}`}
      </Text>
      <Text type="secondary" style={{ fontSize: 12, marginLeft: "auto" }}>
        {formatTime(attempt.restart?.createdAt ?? null, STEP_TIME_FORMAT)}
      </Text>
    </div>
  </div>
);

/**
 * A restart. Not a step the agent took for the patient — a fact about the RUN —
 * so it reads as a boundary rather than a row: the steps above it ran on the
 * old build, the ones below re-ran on the new one.
 *
 * Shown rather than hidden because a run that repeats itself with no
 * explanation reads as a bug. The versions are the whole content: they say a
 * release, not a retry, is why.
 */
const RestartMarker = ({ restart, t }) => (
  <div
    style={{
      display: "flex",
      alignItems: "flex-start",
      gap: 10,
      margin: "16px 0 4px -6px",
      padding: "11px 13px",
      background: PRIMARY_50,
      border: `1px solid ${PRIMARY_100}`,
      borderRadius: 6
    }}
    data-testid="agentic-run-restart-marker"
  >
    <ReloadOutlined style={{ fontSize: 14, color: PRIMARY_600, marginTop: 2 }} />
    <div style={{ minWidth: 0 }}>
      <Text style={{ display: "block", fontSize: 13, fontWeight: 500, color: PRIMARY_600 }}>
        {restart.toVersion
          ? t("agentic_runs.restarted_on_version", { version: restart.toVersion })
          : t("agentic_runs.restarted")}
      </Text>
      <Text style={{ display: "block", fontSize: 12, color: PRIMARY_600 }}>{t("agentic_runs.restarted_detail")}</Text>
    </div>
  </div>
);

// ─── Awaiting-action CTA banner ───────────────────────────────────────────
// One unified callout for every state where the agent is blocked on a human,
// rendered at the TOP of the latest run's card body (see the `pendingAction`
// derivation in RunDetails):
//   - emr_fill       (waiting_hitl)            → enter EMR data manually
//   - consent_decide (waiting_hitl)            → make the consent decision
//   - ee_review      (waiting_hitl)            → approve an express enrollment
//   - screenshots    (waiting_for_screenshots) → upload EMR screenshots
//   - multiple                                 → several pauses open at once
//
// READ-ONLY: the fill / decision / upload all happen in the Tasks hub, so the
// banner explains WHAT the run is waiting on + offers a single "Open in Tasks"
// button. Same layout throughout; only the icon, accent, copy, and deep-link
// target vary by kind:
//   - the navigator pauses → amber; `?agenticRunId=<id>` so the hub auto-opens
//     the task modal. The id is the row that OWNS the pause, which for
//     emr_fill / ee_review is a child of the run this modal renders.
//   - screenshots → blue; the bare Tasks route (the EMR screenshots upload
//     section is the first, always-expanded card there).
//   - multiple → amber, and deliberately NO id: picking one of several waiting
//     enrollments for the navigator would be a guess, so the hub gets the choice.
//
// consent_decide's heading is the dynamic friendly consent state (+ a "Will
// send via …" line when ready to send); emr_fill / screenshots use the static
// copy below.
// Soft translucent tint of a hex accent — keeps the banner a gentle colored
// callout (low-opacity fill + border) rather than a bold, saturated block.
const withAlpha = (hex, alpha) => {
  const int = parseInt(hex.slice(1), 16);
  return `rgba(${(int >> 16) & 255}, ${(int >> 8) & 255}, ${int & 255}, ${alpha})`;
};

const AWAITING_ACTION_STATIC = {
  [HITL_TASK_TYPES.EMR_FILL]: {
    accent: WARNING_500,
    tint: WARNING_50,
    Icon: FormOutlined,
    headingKey: "agentic_review.emr_fill_heading",
    headingFallback: "EMR data needed",
    subtextKey: "agentic_review.emr_fill_subtext",
    subtextFallback: "Enter the patient's EMR data manually so the agent can continue."
  },
  // The enrollment is prepared and parked on the navigator's approval. Static
  // copy (unlike consent_decide): the pause carries no state string to headline
  // — WHICH enrollment is the panel's title, not the banner's job.
  [HITL_TASK_TYPES.EE_REVIEW]: {
    accent: WARNING_500,
    tint: WARNING_50,
    Icon: SolutionOutlined,
    headingKey: "agentic_review.ee_review_heading",
    headingFallback: "Express enrollment review needed",
    subtextKey: "agentic_review.ee_review_subtext",
    subtextFallback: "Review the prepared enrollment and approve it so the agent can submit it."
  },
  [PENDING_ACTION_SCREENSHOTS]: {
    accent: PRIMARY_600,
    tint: PRIMARY_50,
    Icon: UploadOutlined,
    headingKey: "agentic_review.screenshots_heading",
    headingFallback: "Screenshots needed",
    subtextKey: "agentic_review.screenshots_subtext",
    subtextFallback: "Upload the patient's EMR screenshots in the Tasks page."
  },
  // Submitted, extraction in flight — informational only, no CTA (there is
  // nothing left for the user to do in Tasks).
  [PENDING_ACTION_SCREENSHOTS_PROCESSING]: {
    accent: PRIMARY_600,
    tint: PRIMARY_50,
    Icon: UploadOutlined,
    headingKey: "agentic_review.screenshots_processing_heading",
    headingFallback: "Screenshots submitted",
    subtextKey: "agentic_review.screenshots_processing_subtext",
    subtextFallback: "Extracting and matching the screenshots — this can take a few minutes.",
    hideOpenTasks: true
  }
};

const AwaitingActionBanner = ({ pendingAction, steps, onOpenTasks, t }) => {
  const { kind, agenticRunId, reviewOutcome, waitingCount } = pendingAction;

  // Resolve per-state presentation. The two dynamic states compose their heading
  // here — consent_decide from the friendly consent state (plus a "Will send
  // via …" line when ready to send), the fan-out wait from the count; every
  // other state pulls static copy + visuals from the map above.
  let accent;
  let tint;
  let Icon;
  let heading;
  let subtext = null;
  let hideOpenTasks = false;
  if (kind === PENDING_ACTION_MULTIPLE) {
    accent = WARNING_500;
    tint = WARNING_50;
    Icon = SolutionOutlined;
    heading = t("agentic_review.multiple_heading", { count: waitingCount });
    subtext = t("agentic_review.multiple_subtext");
  } else if (kind === HITL_TASK_TYPES.CONSENT_DECIDE) {
    accent = WARNING_500;
    tint = WARNING_50;
    Icon = SendOutlined;
    heading = friendlyReason(reviewOutcome) || reviewOutcome;
    const consentStep = findConsentStateCarrier(steps);
    const channels = consentStep?.payload?.availableContactChannels;
    subtext =
      isReadyToSendOutcome(reviewOutcome) && Array.isArray(channels) && channels.length > 0
        ? `${t("agentic_review.will_send_via", "Will send via")} ${channels.map(humanizeContactField).join(" or ")}.`
        : null;
  } else {
    const cfg = AWAITING_ACTION_STATIC[kind];
    if (!cfg) return null;
    accent = cfg.accent;
    tint = cfg.tint;
    Icon = cfg.Icon;
    heading = t(cfg.headingKey, cfg.headingFallback);
    subtext = cfg.subtextKey ? t(cfg.subtextKey, cfg.subtextFallback) : null;
    hideOpenTasks = !!cfg.hideOpenTasks;
  }

  return (
    <div
      data-testid="agentic-review-banner"
      style={{
        display: "flex",
        alignItems: "center",
        gap: 14,
        marginBottom: 12,
        padding: "12px 16px",
        border: `1px solid ${withAlpha(accent, 0.35)}`,
        borderRadius: 10,
        background: withAlpha(accent, 0.08)
      }}
    >
      {/* Tinted circular icon badge — amber for a human decision/entry, blue */}
      {/* for an operator upload. */}
      <div
        aria-hidden="true"
        style={{
          width: 38,
          height: 38,
          flexShrink: 0,
          borderRadius: "50%",
          background: tint,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color: accent,
          fontSize: 18
        }}
      >
        <Icon />
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <Text strong style={{ display: "block", color: GRAY_800, fontSize: 14 }}>
          {heading}
        </Text>
        {subtext && (
          <Text
            style={{ display: "block", color: GRAY_600, fontSize: 12, marginTop: 2 }}
            data-testid="agentic-review-subtext"
          >
            {subtext}
          </Text>
        )}
      </div>
      {/* Deep-links to the Tasks hub (the fill / decision / upload surface) and */}
      {/* closes this read-only modal. A single HITL wait passes its agenticRunId so */}
      {/* the hub auto-opens that task's modal; screenshots and a several-at-once */}
      {/* wait pass none and land on the Tasks page. */}
      {/* Hidden while extraction runs — the upload is done, nothing to open. */}
      {!hideOpenTasks && (
        <Button
          type="primary"
          onClick={() => onOpenTasks(agenticRunId)}
          data-testid="agentic-review-open-tasks"
          style={{ flexShrink: 0 }}
        >
          {t("agentic_runs.open_in_tasks")} <ArrowRightOutlined />
        </Button>
      )}
    </div>
  );
};

// The step the workflow's outer catch stamps when the run CRASHES: the in-flight
// step marked failed with the error detail on `payload.error` (no other failed
// step carries that key — gathering failures carry `exitMode`, send failures
// `errorMessage`). It must always render hard-red: the run died here.
const isCrashStep = (step) => step.result === AUDIT_STEP_RESULT.FAILED && typeof step.payload?.error === "string";

// A failed step counts as "recovered" when the run continued past it — i.e. a
// later step exists in execution order. Steps arrive sorted oldest-first (by
// `id` asc), so any step that isn't the last one has a successor and therefore
// the run progressed beyond its failure. A failed step sitting LAST (the run
// stopped there — e.g. an `emr_gathering` `no_screenshots_provided` that handed
// back) is terminal and keeps the hard red error. The CRASH step is never
// "recovered" even though a successor exists — the failure handler itself writes
// the `update_queue` handback after it, which is cleanup, not recovery. Only
// `failed` steps are ever candidates; passed/skipped/in-progress always return false.
const isRecoveredFailure = (step, sortedSteps, index) =>
  step.result === AUDIT_STEP_RESULT.FAILED && !isCrashStep(step) && index < sortedSteps.length - 1;

// ─── Manual-fill detection ────────────────────────────────────────────────
// A run is "manual" (navigator typed the EMR data in via the manual-fill HITL
// task) iff it has an APPROVED `emr_manual_fill` step (`decision === "approve"`).
// There's no parsing in that path, so the standalone `emr_data_parsed` step is
// redundant: instead we fold the EMR data onto this manual-fill step. (The
// screenshot path has no such step and keeps its genuine `emr_data_parsed`
// step.) A property of the whole run, so it's computed once at the run level
// and passed down.
const isManualFillStep = (step) =>
  step.stepName === AGENTIC_STEP_NAMES.EMR_MANUAL_FILL && step.payload?.decision === REVIEW_DECISION.APPROVE;

const findManualFillStep = (steps) => steps.find(isManualFillStep) || null;

// ─── Express-enrollment grouping ──────────────────────────────────────────
// A journey is a TREE, and enrollment is the one phase that FANS OUT: the
// conductor spawns an express-enrollment child per enrollable pap, and this
// modal renders every child's steps merged into one list. Left flat, a patient
// with two enrollable programs reads as "#5 availability · #6 decision · #7
// availability · #8 decision …" — six interleaved rows, no way to tell which
// program owns which, and a "#N" implying one sequence that never existed. So
// the EE steps come out of the chronological list and go back as one collapsed
// panel per enrollment.
//
// `ee_config_check` deliberately STAYS in the chronological list: it is the gate
// that decides whether an enrollment happens at all, so it reads as part of the
// main flow — the last thing the conductor establishes before handing off. Only
// the steps that belong to the enrollment ITSELF get grouped.
const EXPRESS_ENROLLMENT_STEP_NAMES = [AGENTIC_STEP_NAMES.EE_HUMAN_REVIEW, AGENTIC_STEP_NAMES.EE_SUBMIT];

const isExpressEnrollmentStep = (step) => EXPRESS_ENROLLMENT_STEP_NAMES.includes(step.stepName);

// Groups EE steps into one entry per enrollment, keyed on `agenticRunId` — the
// enrollment child's OWN run row, which every step it writes carries — and NOT
// on `payload.papId`. papId only survives on the steps the workflow writes once:
// the review step is written at the pause and REWRITTEN on the decision, and
// that second write carries the decision fields alone, so a decided review has
// no papId to group by. Keying on it would drop every enrollment's decision into
// one shared `undefined` bucket — exactly the confusion this grouping exists to
// remove. `agenticRunId` is also the id the pause's Tasks deep-link needs, so
// the group and the CTA agree on what identifies an enrollment.
//
// `steps` are the ones to group (already in execution order); `allSteps` is the
// whole run, which the identity lookup needs — see below. Groups come back in
// the order their first step ran, and each group's steps keep that order.
const groupEnrollmentSteps = (steps, allSteps) => {
  const byRun = new Map();
  steps.forEach((step) => {
    const group = byRun.get(step.agenticRunId);
    if (group) {
      group.steps.push(step);
      return;
    }
    byRun.set(step.agenticRunId, { agenticRunId: step.agenticRunId, steps: [step] });
  });

  return [...byRun.values()].map((group) => {
    // Identity is read from EVERY step the enrollment wrote, not just the two
    // grouped ones, because none of the grouped two reliably carries it: the
    // review step is rewritten on the decision and keeps only decision fields,
    // and `ee_submit` never held the names. `ee_config_check` — which renders up
    // in the main flow — is the step that recorded them, and it shares this
    // enrollment's `agenticRunId`. Scoping the lookup to the group would leave
    // every DECIDED enrollment titled with no name, and two of them
    // indistinguishable.
    //
    // The program + drug are a deliberate write-time SNAPSHOT (a later rename
    // must not rewrite what the agent acted on), so they're read from the run's
    // own steps rather than looked up live.
    const ownSteps = allSteps.filter((s) => s.agenticRunId === group.agenticRunId);
    const named = ownSteps.find((s) => s.payload?.programName || s.payload?.drugName)?.payload;
    const identified = ownSteps.find((s) => s.payload?.papId != null)?.payload;
    return {
      ...group,
      papId: identified?.papId ?? null,
      programName: named?.programName ?? null,
      drugName: named?.drugName ?? null,
      // The decision row, and the submission it absorbs (see `stepSummary`'s
      // EE_HUMAN_REVIEW branch).
      reviewStep: group.steps.find((s) => s.stepName === AGENTIC_STEP_NAMES.EE_HUMAN_REVIEW) ?? null,
      submitStep: group.steps.find((s) => s.stepName === AGENTIC_STEP_NAMES.EE_SUBMIT) ?? null
    };
  });
};

/**
 * The recommended programs, split into the three piles a navigator actually has:
 * what the agent is taking, what they must enroll themselves, and what is
 * already under way (which is NOT work, and must not be mixed into the second
 * pile — that is the difference between a real to-do list and a count).
 *
 * Each row is labelled `Program (Drug)` — the same shape the Tasks hub and the
 * enrollment panels use, so the surfaces read as one thing. An unnamed program
 * degrades to its id rather than to "undefined".
 */
const EnrollmentCandidatesList = ({ candidates, enrollmentGroups = [] }) => {
  const label = (c) => {
    const name = c.programName || `Program #${c.assistanceProgramId}`;
    // Most manufacturer copay programs already carry the drug in their name
    // ("… Co-Pay Assistance Program (Opdivo)"), so appending it unconditionally
    // printed the drug twice.
    if (!c.drugName || name.toLowerCase().includes(c.drugName.toLowerCase())) return name;
    return `${name} · ${c.drugName}`;
  };
  // What became of the enrollment the agent took for this pap. The candidates
  // payload is written BEFORE the fan-out, so on its own it can only promise;
  // pairing it with the child's own steps is what lets the row report instead.
  const outcomeFor = (papId) => {
    const group = enrollmentGroups.find((g) => g.papId === papId);
    if (!group?.reviewStep) return null;
    const p = group.reviewStep.payload || {};
    const approver = formatUserName(p.user);
    // The badge carries the STATE, the line carries the attribution — so neither
    // repeats the other, and a reader scanning several programs gets the state
    // without reading sentences.
    if (p.decision === REVIEW_DECISION.DECLINE) {
      return { badge: "Declined", tone: STEP_TONE.neutral, line: approver ? `By ${approver}` : null };
    }
    if (group.reviewStep.result === AUDIT_STEP_RESULT.TIMED_OUT) {
      return { badge: "Not reviewed in time", tone: STEP_TONE.neutral, line: "Nothing was created or submitted" };
    }
    if (p.decision !== REVIEW_DECISION.APPROVE) return null;
    if (group.submitStep?.result === AUDIT_STEP_RESULT.FAILED) {
      // The application and its filled form DO survive an approval whose submit
      // failed — unlike a timeout, where nothing was ever created.
      return {
        badge: "Submission failed",
        tone: STEP_TONE.failure,
        line: approver ? `Approved by ${approver} — the filled application is saved` : "The filled application is saved"
      };
    }
    return {
      badge: "Enrolled",
      tone: STEP_TONE.success,
      line: approver ? `Approved by ${approver}` : null
    };
  };

  // When the decision was made — the one fact the enrollment's own panel used to
  // carry that this row didn't. `decidedAt` over `createdAt`: the pause OPENED at
  // createdAt and the human answered later.
  const decidedAtFor = (papId) => {
    const group = enrollmentGroups.find((g) => g.papId === papId);
    const when = group?.reviewStep && stepTimestamp(group.reviewStep);
    // Keeps the null (not formatTime's "—") — the caller renders nothing at all
    // when there is no decision yet.
    return when ? formatTime(when, SHORT_TIME_FORMAT) : null;
  };

  const groups = [
    {
      // Tense-neutral: this same row is read while the agent is still waiting for
      // approval and long after it finished.
      key: "agent",
      title: "Agent is handling",
      color: SUCCESS_500,
      rows: candidates.filter((c) => c.enrollable),
      // The ONLY group whose rows differ in state — enrolled / waiting / declined
      // / failed / not reviewed — which is what makes a per-row badge worth its
      // space here and pure noise in the groups below, where the heading already
      // says the state for every row in them.
      badge: (c) => outcomeFor(c.papId) || { badge: "Waiting for approval", tone: STEP_TONE.attention, line: null },
      note: (c) => {
        const outcome = outcomeFor(c.papId);
        if (!outcome) return "Waiting for your approval before it submits";
        const when = decidedAtFor(c.papId);
        return [outcome.line, when].filter(Boolean).join(" · ") || null;
      }
    },
    {
      key: "you",
      title: "Needs you",
      color: WARNING_500,
      rows: candidates.filter((c) => !c.enrollable && !isNoActionSkip(c.skipReason)),
      note: (c) => SKIP_REASON_TEXT[c.skipReason] || "Needs a person to review"
    },
    {
      key: "none",
      title: "Nothing to do",
      color: GRAY_500,
      rows: candidates.filter((c) => !c.enrollable && isNoActionSkip(c.skipReason)),
      note: (c) => SKIP_REASON_TEXT[c.skipReason]
    }
  ].filter((g) => g.rows.length > 0);

  return (
    <div data-testid="agentic-enrollment-candidates" style={{ marginTop: 8 }}>
      {groups.map((group) => (
        <div key={group.key} style={{ marginTop: 8 }}>
          <Text style={groupLabelStyle(group.color)}>{group.title}</Text>
          {group.rows.map((c) => (
            <div key={c.papId} style={{ marginTop: 4 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
                <Text style={{ fontSize: 12 }}>{label(c)}</Text>
                {group.badge &&
                  (() => {
                    const b = group.badge(c);
                    return b ? (
                      <span
                        style={{
                          background: b.tone.bg,
                          color: b.tone.fg,
                          padding: "1px 6px",
                          borderRadius: 10,
                          fontSize: 10,
                          fontWeight: 600,
                          whiteSpace: "nowrap"
                        }}
                      >
                        {b.badge}
                      </span>
                    ) : null;
                  })()}
              </div>
              <Text type="secondary" style={{ fontSize: 11, display: "block" }}>
                {group.note(c)}
              </Text>
            </div>
          ))}
        </div>
      ))}
    </div>
  );
};

// remaining_oop_calculation that produced no coverage figures: the step ran but
// had nothing to compute against. We surface it as SKIPPED (grey icon + an
// "N/A — skipped" summary) rather than a passed ✓ with a bare "N/A".
/**
 * When a step HAPPENED, for display. `createdAt` is when the row was written —
 * for a HITL pause that is when it OPENED, which on an audit trail misdates the
 * human's action by however long they took to answer (minutes, or days). The
 * workflow stamps `decidedAt` on the payload when the decision lands; prefer it.
 */
const stepTimestamp = (step) => step.payload?.decidedAt || step.createdAt;

/**
 * Splices a standalone reason sentence into a clause after an em dash. Only the
 * first character is touched, so acronyms and proper nouns mid-sentence survive.
 */
const lowerFirst = (text) => (text ? text.charAt(0).toLowerCase() + text.slice(1) : text);

const hasNoOopData = (step) =>
  step.stepName === AGENTIC_STEP_NAMES.REMAINING_OOP_CALCULATION &&
  !(Array.isArray(step.payload?.remainingOops) && step.payload.remainingOops.some((v) => v != null));

/**
 * Cross-step context every row may need, resolved ONCE per run and handed down
 * as one object.
 *
 * It used to be six separate props. They exist because a row often folds in
 * something ANOTHER step recorded — the manual-fill row shows the `emr` step's
 * data, the consent decision shows the consent-state step's context, the
 * closed-programs row names its paps from the eligibility step's candidates —
 * so the values are run-level, not row-level, and threading them individually
 * made the call site read like a configuration file.
 *
 * Each row still reads only the one or two fields its own step cares about;
 * every other field is ignored.
 */
const StepCard = ({
  step,
  recoveredFromFailure,
  context = {},
  patientId,
  // Rows inside an enrollment branch — smaller marker, no separator. NOTHING
  // passes it today (enrollments render inside the `enrollment_candidates` row
  // instead), so this path is unreached; kept because the shape is still the
  // right one if branch rows return.
  nested = false,
  // Whether this row's evidence starts open. Decided by the caller, because it
  // depends on the ROW's place in the run — the current attempt's findings are
  // worth showing, a superseded attempt's are not.
  detailOpen = false,
  t
}) => {
  const {
    emrData,
    consentStateContext,
    consentSend,
    enrollmentGroups = [],
    papLabels,
    // Read by the enrollment summary, and NOTHING supplies it today — the caller
    // has never passed it, so those branches are unreached. Kept in the shape
    // rather than deleted, so this refactor changes no behaviour; whether the
    // summary still wants it is its own question.
    enrollmentSubmission,
    // The site's `statuses`, resolved once by RunDetails — StepCard is rendered
    // per step, so subscribing here would open one selector per row.
    patientStatuses = []
  } = context;
  const isPhiAllowed = useSelector(selectIsPhiAllowed);
  const [screenshotsOpen, setScreenshotsOpen] = useState(false);
  const [expanded, setExpanded] = useState(detailOpen);
  // Screenshots the agent extracted from live on the EMR-gathering step (that's
  // where they were captured). PHI-gated — the images are raw EMR pages. Only on
  // a PASSED step: while gathering is in flight (result == null, "Processing
  // screenshots") the refs don't exist yet, so the button would open a
  // misleading empty viewer; a FAILED gathering has nothing usable either.
  const canViewScreenshots =
    step.stepName === AGENTIC_STEP_NAMES.EMR_GATHERING && step.result === AUDIT_STEP_RESULT.PASSED && isPhiAllowed;
  // Completed steps map their `result` to the matching visual (SKIPPED
  // falls back to the generic grey "Skipped" pill); in-progress steps
  // (`result == null`) resolve to a running / human-wait visual. A FAILED step
  // the run continued past (recoveredFromFailure) downgrades the hard red error
  // to an orange warning — the failure was overcome, not terminal (see
  // RECOVERED_FAILURE_VISUAL / the `recoveredFromFailure` derivation in RunDetails).
  let visual;
  if (step.result == null) {
    visual = visualForInProgress(step);
  } else if (enrollmentSubmission?.result === AUDIT_STEP_RESULT.FAILED) {
    // A folded submission that failed. The review step itself PASSED — the
    // navigator did decide — so its own result would paint a green check over an
    // enrollment that never reached the program. This row speaks for the whole
    // event, so it takes the event's worst outcome and the failure stays visible
    // without expanding anything.
    visual = STEP_RESULT_VISUAL[AUDIT_STEP_RESULT.FAILED];
  } else if (step.result === AUDIT_STEP_RESULT.FAILED && recoveredFromFailure) {
    visual = RECOVERED_FAILURE_VISUAL;
  } else if (step.result === AUDIT_STEP_RESULT.PASSED && hasNoOopData(step)) {
    // OOP calc ran with no coverage figures → nothing computed; show as skipped.
    visual = STEP_RESULT_VISUAL[AUDIT_STEP_RESULT.SKIPPED];
  } else {
    visual = STEP_RESULT_VISUAL[step.result] || STEP_RESULT_VISUAL[AUDIT_STEP_RESULT.SKIPPED];
  }
  const Icon = visual.icon;
  const summary = stepSummary(step, enrollmentSubmission, t, consentSend, patientStatuses);
  const payload = step.payload || {};

  // Where to render the human-readable EMR breakdown (insurances + meds) and
  // which payload backs it:
  //   - manual-fill step → the run's `emr` step payload (`emrData`), folded
  //     here because the standalone `emr` step is suppressed for manual runs.
  //   - screenshot `emr` step → its own payload.
  // Both render the EMR data as a navigator-readable list (insurances + meds)
  // rather than raw fields — the breakdown IS the detail; `extractedAt` / the
  // HITL bookkeeping fields aren't navigator-facing and simply aren't shown.
  const manualFill = isManualFillStep(step);
  const emrBreakdownPayload = manualFill
    ? emrData
    : step.stepName === AGENTIC_STEP_NAMES.EMR_DATA_PARSED
    ? payload
    : null;
  const showEmrBreakdown = hasEmrData(emrBreakdownPayload);

  // Consent fold: on the consent-decision step we surface the standalone
  // consent step's "ready to send" / channel context (`consentStateContext`,
  // computed at the run level) as light SECONDARY text — the decision line
  // ("Declined by X — Reason: …" / "Approved by X" / "Awaiting your decision")
  // stays primary. Only the consent_decide `human_review` step receives this
  // prop; render it only when there's context to show.
  const isConsentDecideStep = step.stepName === AGENTIC_STEP_NAMES.CONSENT_HUMAN_REVIEW;
  const showConsentContext = isConsentDecideStep && !!consentStateContext;
  // The consent-decision step's secondary line is ALWAYS the folded consent state
  // context (rendered below), never the step's own friendly reason — the latter
  // is the pre-decision consent STATE (incl. "missing contact info"), which is
  // stale once the navigator can fill it at approval. So a consent_decide step
  // emits no friendlyLine; a suppressed/empty context simply renders nothing.
  // Every other step keeps its friendly reason.
  const friendlyLine = isConsentDecideStep ? null : friendlyReasonForStep(step);

  // The four blocks below the summary are this step's evidence. Stated once,
  // here, so the disclosure control and the panel can never disagree about
  // whether there is anything to show.
  const hasDetail =
    (step.stepName === AGENTIC_STEP_NAMES.FINANCIAL_ASSISTANCE_ELIGIBILITY &&
      Array.isArray(payload?.reasonChecks) &&
      payload.reasonChecks.length > 0) ||
    (step.stepName === AGENTIC_STEP_NAMES.ENROLLMENT_CANDIDATES &&
      Array.isArray(payload?.candidates) &&
      payload.candidates.length > 0) ||
    (step.stepName === AGENTIC_STEP_NAMES.INELIGIBLE_ROWS_COMPLETED &&
      Array.isArray(payload?.closedRows) &&
      payload.closedRows.length > 0) ||
    !!showEmrBreakdown;

  return (
    <div
      data-testid={`agentic-step-row-${step.stepName}`}
      style={{
        position: "relative",
        padding: "7px 0",
        // A hairline per row. The rail says "one thread"; the separators say
        // where one step ends — without them a row with a summary, a wait and an
        // open panel runs into the next one.
        borderBottom: nested ? "none" : `1px solid ${GRAY_100}`
      }}
    >
      <div>
        <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
          {/* At the head of its row. It used to be pulled out of the row and
              positioned onto the rail; with no rail to land on it sits inline,
              where a row that grows tall no longer drags it away from anything.
              A running step keeps its spinner — that one has to animate. */}
          {(() => {
            const Glyph = !visual.running && step.result === AUDIT_STEP_RESULT.PASSED ? TIMELINE_PASSED_ICON : Icon;
            return (
              <Glyph spin={!!visual.running} style={{ fontSize: nested ? 13 : 14, color: visual.fg, flexShrink: 0 }} />
            );
          })()}
          <Text style={{ color: GRAY_800, fontSize: 14 }}>
            {step.stepName === AGENTIC_STEP_NAMES.CONSENT_HUMAN_REVIEW ||
            step.stepName === AGENTIC_STEP_NAMES.EMR_MANUAL_FILL
              ? humanReviewLabel(step, t)
              : humanizeStepName(step.stepName)}
          </Text>
          {/* Only on rows that HAVE evidence — a chevron that opens nothing is a */}
          {/* dead control, and most steps carry no payload worth a panel. */}
          {hasDetail && (
            <Button
              type="text"
              size="small"
              data-testid={`agentic-step-detail-toggle-${step.stepName}`}
              aria-expanded={expanded}
              aria-label={t(expanded ? "agentic_runs.hide_detail" : "agentic_runs.show_detail")}
              onClick={() => setExpanded((open) => !open)}
              icon={<RightOutlined rotate={expanded ? 90 : 0} style={{ fontSize: 11 }} />}
              style={{ padding: 0, height: 18, width: 18, minWidth: 18, color: GRAY_500 }}
            />
          )}
          {/* Result chip only for in-progress steps ("Running" / "Awaiting"). */}
          {/* Completed steps drop the "Passed"/"Failed" pill — the leading */}
          {/* icon (blue check / red x) + the verdict pill already convey it. */}
          {/* SUPERSEDED is the exception: it carries no verdict pill (there was */}
          {/* no verdict — the restart took the step away mid-flight), so without */}
          {/* the chip the row would say nothing at all about why it stopped. */}
          {(step.result == null || step.result === AUDIT_STEP_RESULT.SUPERSEDED) && <StatusChip visual={visual} />}
          {/* Verdict pill is the eligibility-gate signal: separate from */}
          {/* the technical result above. Only rendered for steps where */}
          {/* the verdict is meaningful (financial-assistance / oop / */}
          {/* consent) — data-fetching and side-effect steps don't have */}
          {/* a gate outcome. */}
          {(() => {
            const verdict = stepVerdict(step, recoveredFromFailure, t);
            if (!verdict) return null;
            return (
              <div
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  // KEPT COLOURED. The leading icon says pass or fail; the
                  // pill says which KIND — red failure, amber attention, green
                  // success — and that four-tone map is asserted in its own
                  // test block. Neutralising it lost a distinction the icon
                  // cannot carry.
                  background: verdict.bg,
                  color: verdict.fg,
                  padding: "1px 8px",
                  borderRadius: 4,
                  fontSize: 12,
                  fontWeight: 500
                }}
              >
                {verdict.label}
              </div>
            );
          })()}
          {/* Time only. Every row repeating "Sep 20, 2026" pushed the step's own
              moment to the far right of a wide column and said nothing — the run
              header already carries the date. */}
          <Text type="secondary" style={{ fontSize: 12, marginLeft: "auto" }}>
            {formatTime(stepTimestamp(step), STEP_TIME_FORMAT)}
          </Text>
          {canViewScreenshots && (
            <Tooltip title={t("agentic_board.consent.view_screenshots")}>
              <Button
                size="small"
                type="text"
                icon={<FileImageOutlined />}
                onClick={() => setScreenshotsOpen(true)}
                data-testid="agentic-run-view-screenshots"
              />
            </Tooltip>
          )}
        </div>
        {canViewScreenshots && (
          <EmrScreenshotsViewer
            patientId={patientId}
            open={screenshotsOpen}
            onClose={() => setScreenshotsOpen(false)}
          />
        )}
        {summary && (
          <Text
            data-testid={`agentic-step-summary-${step.stepName}`}
            style={{ color: GRAY_600, fontSize: 13, display: "block", marginTop: 3 }}
          >
            {summary}
          </Text>
        )}
        {/* Friendly explanation only — we never render the raw snake_case */}
        {/* reason code in the user-facing card. */}
        {step.outcome && friendlyLine && (
          <Text style={{ color: GRAY_600, fontSize: 13, display: "block", marginTop: summary ? 3 : 3 }}>
            {friendlyLine}
          </Text>
        )}
        {/* Folded consent state context — the standalone consent step's */}
        {/* "ready to send" / can-send channel line, shown as lighter secondary */}
        {/* text beneath the primary decision line so the consent decision is a */}
        {/* single event rather than two separate steps. */}
        {showConsentContext && (
          <Text
            data-testid="agentic-consent-state-context"
            style={{ color: GRAY_500, fontSize: 12, display: "block", marginTop: summary || step.outcome ? 4 : 6 }}
          >
            {consentStateContext}
          </Text>
        )}
        {/* The step's EVIDENCE, behind a disclosure. The row above always states */}
        {/* its conclusion — "5 meds · 2 plans", "1 of 1 reason eligible" — so a */}
        {/* reader gets the answer without a medication list, and expands only to */}
        {/* check the working. Always-open is what made a restarted run's timeline */}
        {/* unreadable: two attempts' full breakdowns, one after the other. */}
        {hasDetail && expanded && (
          // Inset, on its own ground: the evidence is a panel the row opens, not
          // more of the row. Without a container the criteria tree and the EMR
          // list read as loose text hanging under the summary.
          <div
            data-testid={`agentic-step-detail-${step.stepName}`}
            style={{
              marginTop: 8,
              padding: "10px 12px",
              background: GRAY_50,
              border: `1px solid ${GRAY_100}`,
              borderRadius: 6
            }}
          >
            {/* Financial-assistance step: per-reason verdicts rendered inline. Each */}
            {/* reason carries its own criteria checklist (with the drug / PAP */}
            {/* breakdown nested under the relevant criterion), so it's self-contained. */}
            {step.stepName === AGENTIC_STEP_NAMES.FINANCIAL_ASSISTANCE_ELIGIBILITY &&
              Array.isArray(payload?.reasonChecks) &&
              payload.reasonChecks.length > 0 && <ReasonChecksList checks={payload.reasonChecks} />}
            {/* Which programs the agent can enroll and which need a person. Grouped */}
            {/* by WHO ACTS, not by status: the count says how much is covered, the */}
            {/* groups say whose job the rest is — which is the actionable half. */}
            {step.stepName === AGENTIC_STEP_NAMES.ENROLLMENT_CANDIDATES &&
              Array.isArray(payload?.candidates) &&
              payload.candidates.length > 0 && (
                <EnrollmentCandidatesList candidates={payload.candidates} enrollmentGroups={enrollmentGroups} />
              )}
            {/* Which programs the agent closed at discovery. Its rows are resolved */}
            {/* work, so they never reach the handoff row below — this is the only */}
            {/* place the run says what became of them. */}
            {step.stepName === AGENTIC_STEP_NAMES.INELIGIBLE_ROWS_COMPLETED &&
              Array.isArray(payload?.closedRows) &&
              payload.closedRows.length > 0 && (
                <ClosedProgramsList
                  closedRows={payload.closedRows}
                  resolutionReason={payload.resolutionReason}
                  papLabels={papLabels}
                />
              )}
            {/* Human-readable EMR data (insurances + meds) — on the manual-fill */}
            {/* step for manual runs, on the `emr` step for screenshot runs — so */}
            {/* non-technical navigators read coverage + meds in plain language. */}
            {showEmrBreakdown && (
              <EmrDataBreakdown
                payload={emrBreakdownPayload}
                testIdPrefix={manualFill ? "agentic-manual-fill" : "agentic-emr"}
                t={t}
              />
            )}
          </div>
        )}
      </div>
    </div>
  );
};

const RunDetails = ({ run, patientId, t, onOpenTasks }) => {
  // Status labels for the start-of-run row. Resolved here and passed down: the
  // audit payload stores wire values, and StepCard renders once per step.
  const [patientStatuses] = useGetSetting(["statuses"]);
  // `run.steps` is an append-only LOG: a step that parked and later resolved
  // arrives as several events. Everything that asks "what did this step do?"
  // wants the step's CURRENT state, so reduce once here — every `steps.find` /
  // `steps.some` below then reads the newest event, not the stale waiting one.
  // `agentStepEvents` drops run-level bookkeeping (a restart boundary) so no
  // reader below has to know about it — and it is shared with the patient-page
  // button, which reads the same log for its "last activity" stamp.
  //
  // Memoized because it returns a NEW array each render, which would otherwise
  // defeat the two memos below that take `stepEvents` as their dep.
  const stepEvents = useMemo(() => agentStepEvents(run), [run]);
  const steps = useMemo(() => currentAgentSteps(stepEvents), [stepEvents]);
  // The journey's restarts, as timeline rows and as the boundaries the waits
  // below are measured within. Read from the RAW log — `agentStepEvents` drops
  // `run_resumed` on purpose, because a restart is not activity the agent did
  // for this patient, and the patient-page "last activity" stamp must keep
  // reading it that way.
  // The log split into ATTEMPTS. `steps` above stays the run-wide collapse —
  // it answers "what is true NOW" for the banner, the folds and the status —
  // while the TIMELINE has to show what happened, which is a different
  // question: a restart re-runs the journey, so the same step name exists once
  // per attempt and the run-wide collapse keeps only the newest. That deleted
  // the earlier attempt's row and rendered as "manual fill -> consent ->
  // [restarted] -> eligibility", every row in order with half of them missing.
  const attempts = useMemo(() => runAttempts(run), [run]);
  // Which superseded attempts the reader has opened. Keyed by attempt index,
  // which is stable for a given run.
  const [openAttempts, setOpenAttempts] = useState(() => new Set());
  const toggleAttempt = (index) =>
    setOpenAttempts((open) => {
      const next = new Set(open);
      if (next.has(index)) next.delete(index);
      else next.add(index);
      return next;
    });

  // How long each step spent waiting, straight from its own events: the gap
  // between the first one that opened it (`result: null`) and its newest. This is
  // the wait the timeline draws BEFORE the step's row — where it belongs, because
  // it is time that step spent, not dead time after it. `emr_gathering` can hold
  // three events (waiting → processing → terminal); first-to-last is the whole
  // gathering, which is the number an operator is asked about.
  //
  // Measured WITHIN one attempt. A restart re-runs the journey, so the same step
  // opens again, and pairing the first attempt's opener with the second
  // attempt's answer invents a duration neither wait had: a consent decision
  // asked at 14:14, restarted at 14:41 and answered at 15:00 read as "waited
  // 46m" for a wait that actually ran 19. Only the newest attempt is shown, so
  // only its span is wanted.
  // Keyed by step ID, not step NAME: a restarted run holds the same name once
  // per attempt, and one shared key would give both rows the same duration.
  const waitByStepId = useMemo(() => {
    const spans = new Map();
    for (const attempt of attempts) {
      for (const { step, wait } of attempt.steps) {
        if (wait) spans.set(step.id, gapMs(wait.from, stepTimestamp(wait.to)));
      }
    }
    return spans;
  }, [attempts]);

  // Pending-action context — the generalized "the agent is blocked on a human"
  // state that drives the top-of-body CTA banner. Pre-computed at the run level
  // (it's a property of the whole run, not any single step). Two families:
  //   - The navigator waits: the pausing workflow writes its HITL step with
  //     `result === null` and resolves it on decision/timeout, so an open one
  //     means a human is holding that workflow. `findOpenNavigatorPauses` reads
  //     them across the whole TREE — `emr_fill` and `ee_review` pause on CHILD
  //     rows whose status never reaches this payload, so the steps are the only
  //     place they show up. The step name says which kind (consent's `outcome`
  //     carries the consent state for the heading).
  //   - The screenshots operator-wait: NOT a navigator pause (it lives inside
  //     EMR gathering and is resumed by an upload), so it's keyed off the
  //     gathering step's own in-progress outcome.
  // This is always the latest run (the modal shows no others); `null` once the
  // run finished / never paused.
  const openPauses = useMemo(() => findOpenNavigatorPauses(run), [run]);

  // Every step scan the body needs, computed once per steps/run change instead
  // of on each render.
  const { pendingAction, hasApprovedManualFill } = useMemo(() => {
    const isProcessingScreenshots = steps.some(
      (s) =>
        s.stepName === AGENTIC_STEP_NAMES.EMR_GATHERING && s.outcome === EMR_GATHERING_OUTCOME.PROCESSING_SCREENSHOTS
    );
    // Screenshot state comes from the STEP, not the run row: the run we render
    // is the journey root, and the `emr_screenshots` pause lives on the
    // screenshot-collection child's OWN row. Shared with the status calculator
    // so this rule has one definition — and it reads CURRENT events, so a wait
    // the upload already closed stops matching (the raw `.some()` this replaced
    // kept matching the original waiting row forever).
    const isAwaitingScreenshots = isOpenScreenshotWait(steps);
    const approvedManualFill = steps.some(
      (s) => s.stepName === AGENTIC_STEP_NAMES.EMR_MANUAL_FILL && s.payload?.decision === REVIEW_DECISION.APPROVE
    );

    let action = null;
    if (openPauses.length > 1) {
      // A fan-out can park several enrollments on the navigator at once, and
      // then no single task is THE task — deep-linking to one of them would
      // pick for them. Name the count and let the hub present the choice.
      action = { kind: PENDING_ACTION_MULTIPLE, waitingCount: openPauses.length };
    } else if (openPauses.length === 1) {
      const [pause] = openPauses;
      action = {
        kind: NAVIGATOR_PAUSE_TASK_TYPE_BY_STEP[pause.stepName],
        // The row that OWNS the pause, not the journey root we render. The Tasks
        // hub keys its rows on whichever run is `waiting_hitl` — that's this
        // conductor row for consent_decide, but a child row for emr_fill and
        // ee_review. Passing the root's id there would deep-link to a run the hub
        // has no task for, and the task modal would silently not open.
        agenticRunId: pause.agenticRunId ?? run.id,
        reviewOutcome: pause.outcome
      };
    } else if (isAwaitingScreenshots || isProcessingScreenshots) {
      // Once the operator SUBMITTED, there's nothing left to do in Tasks —
      // the banner flips to a passive "processing" state (no CTA).
      action = { kind: isProcessingScreenshots ? PENDING_ACTION_SCREENSHOTS_PROCESSING : PENDING_ACTION_SCREENSHOTS };
    }
    return { pendingAction: action, hasApprovedManualFill: approvedManualFill };
  }, [steps, run.id, openPauses]);

  // Consent fold: one consent EVENT, not two. The consent-decide HITL
  // pause writes a `human_review` step (the navigator's Send / Don't-send
  // decision) AND there's a separate `consent` state step ("ready to send" +
  // the can-send channels). They describe the same moment, so — mirroring the
  // emr_fill manual fold — when a consent_decide `human_review` step exists we
  // hide the standalone `consent` step and surface its state context (the
  // "ready to send" / can-send channel line) as light secondary context on the
  // decision step, where the decision itself stays primary. The screenshot/no-
  // consent-pause path keeps its standalone `consent` step untouched.
  const consentDecideStep = steps.find((s) => s.stepName === AGENTIC_STEP_NAMES.CONSENT_HUMAN_REVIEW);
  // A signed consent produces a SECOND `consent_state_check`: the post-wake
  // re-check that confirms the consent is really active before enrolling (the
  // accepted signal and the wake are not atomic — a revoke can land between).
  // So this can no longer be `.find()`: folding an arbitrary one onto the
  // decision made the trail contradict itself, e.g. "Approved by X" followed by
  // "Patient already has active consent — navigator can proceed manually" on a
  // run that then went and sent a link. Only the check the navigator actually
  // decided ON — the last one BEFORE the decision — is context for that decision.
  // ONE consent_state_check per run: the pre-send state the navigator decides on.
  // The post-signature re-read was removed on the platform side precisely because
  // a second row of this name read as a run that short-circuited on consent it
  // already had. Still selected by position rather than `.find()` — cheap, and it
  // keeps runs recorded while that second step existed rendering correctly.
  const consentChecks = steps.filter((s) => s.stepName === AGENTIC_STEP_NAMES.CONSENT_STATE_CHECK);
  const consentCheckStep = consentDecideStep
    ? consentChecks.filter((s) => s.id < consentDecideStep.id).pop()
    : consentChecks[0];
  // Since MVP-31712 a paused run has NO standalone check row — the pause row is
  // itself the state carrier, so the "fold" reads that row's own AWAITING event.
  const consentStateCarrier = consentCheckStep ?? findConsentStateCarrier(steps);
  // The consent state context folded onto the decision step — the friendly
  // "Can send via …" / state sentence the standalone `consent` step would have
  // shown, MINUS any "missing contact info" framing (the navigator fills that at
  // approval — `suppressMissingContactInfo`). Only computed when both halves
  // exist (a fold is actually happening); null when the only thing to say was
  // "missing contact info", so the decision step then shows just the decision.
  const consentStateContext =
    consentDecideStep && consentStateCarrier
      ? friendlyReasonForStep(consentStateCarrier, { suppressMissingContactInfo: true })
      : null;

  // The send folded onto the decision — ONLY the happy path. A send that was
  // blocked by a state re-check (active consent, expired link, missing contact
  // info) or that crashed is a genuinely separate event from the approval, so it
  // keeps its own row and its own verdict pill.
  const consentSend =
    consentDecideStep &&
    steps.find(
      (s) =>
        s.stepName === AGENTIC_STEP_NAMES.SEND_CONSENT &&
        s.result === AUDIT_STEP_RESULT.PASSED &&
        s.outcome === SEND_CONSENT_OUTCOME.SENT
    );

  // Steps still parked: their current event never resolved, so the elapsed time
  // has to run to NOW rather than to a second event. Same rule for every kind of
  // wait — a patient signature, a navigator pause, an operator's screenshots —
  // so nothing here is consent-specific.
  //
  // `now` is captured when the run data changes, not on every render, so the
  // figure advances on Refresh rather than ticking live. That matches
  // `formatElapsed`'s minutes-to-days granularity.
  const openWaitByStepName = useMemo(() => {
    // Only for a run that is actually still going. A crash or a terminate can
    // leave an opening event with nothing to resolve it — the crash handler only
    // writes a FAILED event for the stage it was mid-way through — and counting
    // that to `now` would show a dead run "waiting 94d so far" and grow forever.
    // Same call the run pill already makes: a leftover open step on a finished
    // run is stale bookkeeping, and the run's verdict outranks it.
    if (run.completedAt) return new Map();
    const now = new Date();
    const spans = new Map();
    for (const step of steps) {
      if (step.result != null) continue;
      spans.set(step.stepName, gapMs(step.createdAt, now));
    }
    return spans;
  }, [steps, run.completedAt]);

  // Manual-fill fold: a run is manual iff it has an approved
  // emr_fill `human_review` step. The structured EMR data lives on the `emr`
  // step's payload for BOTH paths, so we grab that payload once here. When
  // manual, the standalone `emr` step is redundant (no parsing happened) — it's
  // filtered out of the rendered list below and its EMR data is folded onto the
  // manual-fill step instead, so the data shows on exactly one step. The
  // screenshot path has no manual-fill step and renders the `emr` step as-is.
  const isManual = !!findManualFillStep(steps);
  const emrStepPayload = steps.find((s) => s.stepName === AGENTIC_STEP_NAMES.EMR_DATA_PARSED)?.payload || null;

  return (
    // A white card on the modal's own ground. It used to be grey-filled, which
    // made the step list read as a sunken panel; white lifts it and lets the
    // row hairlines carry the structure.
    <div
      data-testid={`agentic-run-card-${run.id}`}
      style={{
        background: WHITE,
        border: `1px solid ${GRAY_200}`,
        borderRadius: 10,
        padding: "12px 16px 4px",
        marginBottom: 12
      }}
    >
      {/* Minimal run-level header: status pill + start date only. NO workflow
          id (it's a technical string navigators don't need) and NO expand/
          collapse — the latest run's steps render directly below, always
          shown. */}
      <div style={{ padding: "0 0 12px", borderBottom: `1px solid ${GRAY_200}`, marginBottom: 12 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
          {/* Reads the denormalized `audit_run.status` directly — bucket
              vocabulary stays consistent with the dashboard KPIs and the
              patient-page button label. Both surfaces pull from
              `RUN_STATUS_META`, so the modal `<Tag>` and the button
              chip stay in lockstep. Default fallback handles legacy
              rows (no status column) by displaying "RUNNING". */}
          {(() => {
            const meta = getRunStatusMeta(run);
            if (!meta) {
              return (
                <Tag color="default" style={{ margin: 0 }}>
                  {t("agentic_runs.outcome_running")}
                </Tag>
              );
            }
            return (
              // Spinner only where the meta asks for one: a restart is the one
              // status that means "wait", with nothing for anyone to act on.
              <Tag
                color={meta.antdTagColor}
                icon={meta.loading ? <LoadingOutlined /> : undefined}
                style={{ margin: 0 }}
              >
                {runStatusLabel(meta, t)}
              </Tag>
            );
          })()}
          {/* Raw `finalOutcome` code intentionally not rendered in the */}
          {/* header — the friendly sentence below covers it for navigators, */}
          {/* and the code stays in audit_run for analytics. The per-step */}
          {/* passed/failed tally chips were removed: a count of "failed" */}
          {/* steps reads as alarming to a non-technical navigator even when */}
          {/* the run reached a normal outcome (declines, skipped gates). The */}
          {/* status pill + the step list carry the detail. */}
          {/* What the run IS, beside the pill that says what it's doing. A
              restarted run reports its ATTEMPTS rather than a step total — each
              attempt heading below carries its own count, and one summed number
              across attempts is a figure nothing in the list adds up to. */}
          <Text type="secondary" style={{ fontSize: 12 }}>
            {[
              t("agentic_runs.started_at", { at: formatTime(run.createdAt) }),
              attempts.length > 1
                ? t("agentic_runs.attempt_count", { count: attempts.length })
                : t("agentic_runs.step_count", { count: steps.length }),
              attempts.length > 1 ? t("agentic_runs.restart_count", { count: attempts.length - 1 }) : null
            ]
              .filter(Boolean)
              .join(" · ")}
          </Text>
        </div>
      </div>
      <div>
        {/* Awaiting-action banner. Read-only — explains what the run is parked */}
        {/* on (EMR fill / consent decision / screenshots) and links to the */}
        {/* Tasks hub where the navigator resolves it. Sits above the steps */}
        {/* list so it's the first thing the navigator sees. */}
        {pendingAction && (
          <AwaitingActionBanner pendingAction={pendingAction} steps={steps} onOpenTasks={onOpenTasks} t={t} />
        )}
        {/* Friendly explanation for the run's finalOutcome — sentence */}
        {/* form so navigators don't have to decode a code string. */}
        {/* Codes without a mapping skip this row. */}
        {friendlyReason(run.finalOutcome) && (
          <Text style={{ display: "block", marginBottom: 8, fontSize: 13, color: GRAY_800 }}>
            {friendlyReason(run.finalOutcome)}
          </Text>
        )}
        {/* `run.detail` is deliberately NOT rendered here. It's backend-generated
            free text (activity name + deepest cause message), so it can't be
            guaranteed navigator-safe. The friendly sentence above carries the
            outcome, per-step payloads carry the specifics, and the raw string
            stays available on the ops surfaces (dashboard failed-runs panel,
            worker logs). */}
        {/* A crash AFTER an approved manual fill loses nothing — the entered
            data persisted before the resume. Say so, or the navigator assumes
            their fill was lost and re-keys it. */}
        {run.finalOutcome === RUN_FINAL_OUTCOME.FAILURE && hasApprovedManualFill && (
          <Text type="secondary" style={{ display: "block", marginBottom: 8, fontSize: 12 }}>
            The manually entered EMR data was saved and will be reused when the agent runs again.
          </Text>
        )}
        {steps.length === 0 ? (
          <Text type="secondary">{t("agentic_runs.no_steps")}</Text>
        ) : (
          // A list of rows, separated by hairlines. This used to be a vertical
          // rail with every marker hung on it; the separators say where one step
          // ends at least as clearly, and two devices doing one job read as
          // clutter.
          //
          // The rail also carried a BRANCH for enrollments — a `nested` row set
          // sitting on its own border off the main line. Nothing renders that
          // today: enrollments are reported inside the `enrollment_candidates`
          // row by `EnrollmentCandidatesList`, and `nested` is never passed. If
          // branch rows ever come back they need their own left border rather
          // than a rail to hang off.
          <div data-testid={`agentic-run-steps-${run.id}`} style={{ position: "relative" }}>
            {(() => {
              // Oldest-first (ascending): `id` is the chronological
              // auto-increment, so sorting by it gives execution order
              // regardless of the backend's order — which is the order the
              // rail reads in. Sorted once here so each row can also ask
              // whether a failed step has a successor (the run recovered
              // past it).
              // EVERY attempt's steps, oldest first — not the run-wide collapse.
              // `id` is the chronological key (a single auto-increment), so
              // concatenating the attempts in order is already sorted.
              const sortedSteps = attempts.flatMap((attempt) => attempt.steps.map(({ step }) => step));
              // Two folds drop redundant standalone steps; `isRecoveredFailure`
              // still indexes against the FULL `sortedSteps` (the run genuinely
              // recovered past a failure even when a folded row is hidden), so
              // only the rendered list uses the filtered set:
              //   - manual runs hide the standalone `emr_data_parsed` step (its
              //     data folds onto the manual-fill step) — but only a PASSED one.
              //     A failed `emr_data_parsed` step (the run crashed there) must
              //     stay visible, or the step list shows no failure at all
              //     (regression: run 509).
              //   - a consent_decide pause hides the standalone `consent_state_check`
              //     step (its state context folds onto the decision step).
              const visibleSteps = sortedSteps.filter(
                (s) =>
                  !(
                    isManual &&
                    s.stepName === AGENTIC_STEP_NAMES.EMR_DATA_PARSED &&
                    s.result === AUDIT_STEP_RESULT.PASSED
                  ) &&
                  !(consentDecideStep && s === consentCheckStep) &&
                  !(consentSend && s === consentSend)
              );
              // The body is one CHRONOLOGY of two kinds of entry: the
              // conductor's own steps, and an enrollment panel per fanned-out
              // child. Each entry sorts by the id of its earliest step, which
              // puts a panel exactly where that enrollment began — after the
              // availability check that gated it, before the queue close that
              // waited on it — with no step name special-cased anywhere. Two
              // enrollments that started either side of another stage land
              // either side of it too, and a future reordering of the
              // conductor's phases needs no change here.
              //
              // Steps carry no ordinal. They used to show "#N", but that was
              // the step's own id-derived position — it skipped wherever a
              // branch swallowed steps, so a reader saw #4, #5, #7 and assumed
              // rows were missing. The rail conveys order on its own.
              const timelineSteps = visibleSteps.filter((s) => !isExpressEnrollmentStep(s));
              // Computed once and also handed to the recommended-enrollments row, so
              // that row can state what BECAME of each program the agent took rather
              // than what it was about to do. "Agent will enroll" is future tense on
              // a record that is read after the fact.
              const enrollmentGroups = groupEnrollmentSteps(visibleSteps.filter(isExpressEnrollmentStep), sortedSteps);
              // Resolved once per run, not per step: the closed-programs row names
              // its paps from the eligibility step's own candidate list.
              const papLabels = getPapLabelsFromEligibility(sortedSteps);
              // Everything a row may fold in from ANOTHER step, in one object.
              const stepContext = {
                emrData: emrStepPayload,
                consentStateContext,
                consentSend,
                enrollmentGroups,
                papLabels,
                patientStatuses
              };
              // Which attempt each rendered step belongs to, so the rows can be
              // dealt out into sections. Enrollments deliberately get NO entry
              // of their own — each is reported on the `enrollment_candidates`
              // row, so a panel here would repeat it a third time.
              const attemptIdByStepId = new Map();
              attempts.forEach((attempt) =>
                attempt.steps.forEach(({ step }) => attemptIdByStepId.set(step.id, attempt.index))
              );
              const entriesFor = (attempt) =>
                timelineSteps
                  .filter((step) => attemptIdByStepId.get(step.id) === attempt.index)
                  .map((step) => ({ key: `step-${step.id}`, sortId: step.id, step }));

              // First and last timestamp of an entry — a step is a moment, a
              // branch is a span, and a gap is measured between the two nearest
              // edges of consecutive entries.
              //
              // `stepTimestamp`, not raw `createdAt`: a row a human answered
              // carries `decidedAt`, which is what the row DISPLAYS. Measuring
              // from `createdAt` while displaying `decidedAt` drew gaps that
              // contradicted the timestamps printed either side of them.
              const entryStart = (entry) => entry.step && stepTimestamp(entry.step);
              const entryEnd = (entry) => {
                const last = entry.group ? entry.group.steps[entry.group.steps.length - 1] : entry.step;
                return last && stepTimestamp(last);
              };

              // One row of the timeline. `isCurrentAttempt` is not styling — it
              // decides what is still TRUE: only the live attempt's steps can
              // still be running, and only its findings are the journey's
              // current reasoning.
              const renderEntry = (entries, isCurrentAttempt) => (entry, idx) => {
                // A step that parked owns its wait, so that span is what goes
                // above its row. Otherwise the marker means dead time BETWEEN
                // steps. Never both: the parked step's own start already sits
                // where the previous step ended, so adding the between-gap would
                // count the same minutes twice.
                const ownWait = waitByStepId.get(entry.step?.id) ?? 0;
                const gap = ownWait || (idx === 0 ? 0 : gapMs(entryEnd(entries[idx - 1]), entryStart(entry)));
                // Still parked: the wait has no end yet, so it trails the row
                // rather than preceding it. Current attempt only — the map is
                // keyed by step NAME, so a step parked again after a restart
                // would otherwise hang its live "waiting so far" under the
                // earlier attempt's copy, which the restart already closed.
                const openWait = isCurrentAttempt ? openWaitByStepName.get(entry.step?.stepName) ?? 0 : 0;
                return (
                  <React.Fragment key={entry.key}>
                    {gap >= NOTABLE_GAP_MS && <WaitMarker ms={gap} t={t} />}
                    <StepCard
                      patientId={patientId}
                      step={entry.step}
                      recoveredFromFailure={isRecoveredFailure(
                        entry.step,
                        sortedSteps,
                        sortedSteps.indexOf(entry.step)
                      )}
                      context={stepContext}
                      // Open on the CURRENT attempt only: a superseded
                      // attempt's findings were replaced by the ones below it,
                      // so opening them by default shows stale reasoning first.
                      detailOpen={isCurrentAttempt}
                      t={t}
                    />
                    {openWait >= NOTABLE_GAP_MS && <WaitMarker ms={openWait} t={t} open />}
                  </React.Fragment>
                );
              };

              // Superseded attempts fold away; the CURRENT one is the journey's
              // state and stays open. The banner introducing it is the divider
              // between the two, so the current attempt needs no heading of its
              // own — there is nothing below it to tell it apart from.
              const previous = attempts.slice(0, -1);
              const current = attempts[attempts.length - 1];
              const currentEntries = current ? entriesFor(current) : [];
              return (
                <>
                  {previous.length > 0 && (
                    <Text
                      style={{ display: "block", fontSize: 12, color: GRAY_500, padding: "2px 0 4px" }}
                      data-testid="agentic-run-previous-attempts"
                    >
                      {t("agentic_runs.previous_attempts")}
                    </Text>
                  )}
                  {previous.map((attempt) => {
                    const entries = entriesFor(attempt);
                    const open = openAttempts.has(attempt.index);
                    return (
                      <React.Fragment key={`attempt-${attempt.index}`}>
                        <CollapsedAttempt
                          attempt={attempt}
                          stepCount={entries.length}
                          open={open}
                          onToggle={() => toggleAttempt(attempt.index)}
                          t={t}
                        />
                        {open && entries.map(renderEntry(entries, false))}
                      </React.Fragment>
                    );
                  })}
                  {current?.restart && <RestartMarker restart={current.restart} t={t} />}
                  {current && currentEntries.map(renderEntry(currentEntries, true))}
                </>
              );
            })()}
          </div>
        )}
      </div>
    </div>
  );
};

const AgenticRunsModal = ({ visible, onClose, patientId, run, isFetching, error, onRefresh }) => {
  const { t } = useTranslation();
  const history = useHistory();

  // Read-only view of the patient's latest run. The data is owned by the parent
  // (AgenticRunsButton), fetched once on patient-page render and passed in — so
  // opening this modal renders detail with no fetch. `onRefresh` re-pulls it on
  // explicit refresh or after a manual trigger.

  // Deep-link to the Tasks hub for the awaiting run, then close this modal
  // (this modal is read-only history; the fill / decision / upload happens in
  // the hub). HITL waits pass a agenticRunId — the hub reads `?agenticRunId` and auto-opens
  // that run's task modal. The screenshots wait passes none → land on the Tasks
  // page, where the EMR screenshots upload section is the first, always-
  // expanded card.
  const handleOpenTasks = (agenticRunId) => {
    const suffix = agenticRunId != null ? `?agenticRunId=${agenticRunId}` : "";
    history.push(`${Routes.AGENTIC_PLATFORM_TASKS}${suffix}`);
    onClose();
  };

  return (
    <Modal
      // Title row carries the (subtle) refresh, so the body opens straight on
      // the run card — no floating toolbar. `marginRight: 24` keeps the icon
      // clear of antd's close X.
      title={
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginRight: 24 }}>
          <span>{t("agentic_runs.title")}</span>
          <Tooltip title={t("agentic_runs.refresh")}>
            <Button
              icon={<ReloadOutlined />}
              loading={isFetching}
              onClick={onRefresh}
              size="small"
              type="text"
              shape="circle"
              data-testid="agentic-runs-refresh"
              aria-label={t("agentic_runs.refresh")}
            />
          </Tooltip>
        </div>
      }
      open={visible}
      onCancel={onClose}
      footer={null}
      width={920}
      destroyOnClose
      data-testid="agentic-runs-modal"
      // Cap the body height and scroll internally so a patient with
      // many runs doesn't push the modal taller than the viewport.
      // Without this, Antd grows the modal until the entire page
      // starts scrolling. `100vh - 200px` leaves room for the modal
      // chrome and a comfortable margin at top/bottom.
      // Grey ground so the white run card reads as a card. White-on-white
      // left only the hairline border to carry it, which barely registered.
      bodyStyle={{ background: GRAY_50, paddingTop: 12, maxHeight: "calc(100vh - 200px)", overflowY: "auto" }}
    >
      {isFetching && <Skeleton active paragraph={{ rows: 4 }} />}
      {error && !isFetching && (
        <Alert
          type="error"
          message={t("agentic_runs.load_error")}
          description={t("agentic_runs.load_error_detail")}
          showIcon
        />
      )}
      {!isFetching && !error && !run && <Empty description={t("agentic_runs.empty")} />}
      {!isFetching && !error && run && (
        // Product surfaces only the patient's latest run — never a history.
        // Re-key on patientId so reopening for a different patient remounts.
        <div key={`agentic-runs-${patientId}`}>
          <RunDetails run={run} patientId={patientId} t={t} onOpenTasks={handleOpenTasks} />
        </div>
      )}
    </Modal>
  );
};

export default AgenticRunsModal;
